import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Alert,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Snackbar,
  CircularProgress,
  InputAdornment
} from "@mui/material";
import {
  ContactMail as ContactIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Cancel as RejectIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import axios from "axios";
import { encryptText, encryptAppId, decryptText, decryptAppId, } from "../../../utils/encryption";
import StageService from '../../../services/stageService';
import { getCountries, getStates, getCities } from "../../../services/api";
import DoApiService from "../../../services/DoApiService";


const AssessorAllotment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getCabStageFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedCabStage = searchParams.get("cabStage");
    if (encryptedCabStage) {
      try {
        const decryptedCabStage = decryptText(encryptedCabStage);
        return decryptedCabStage;
      } catch (error) {
        return encryptedCabStage;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getCabStage = () => {
    const queryCabStage = getCabStageFromQuery();
    if (queryCabStage) {
      return queryCabStage;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());

  // State management
  const [isIntegratedAssessment, setIsIntegratedAssessment] = useState("No");
  const [oneMoreSlot, setOneMoreSlot] = useState("No");
  const [reason, setReason] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [additionalStartDate, setAdditionalStartDate] = useState("");
  const [additionalEndDate, setAdditionalEndDate] = useState("");
  const [openCabDialog, setOpenCabDialog] = useState(false);

  // Dialog states
  const [openAssessorDialog, setOpenAssessorDialog] = useState(false);
  const [dialogCapacity, setDialogCapacity] = useState("");
  const [dialogStartDate, setDialogStartDate] = useState("");
  const [dialogEndDate, setDialogEndDate] = useState("");
  const [dialogSelectedAssessor, setDialogSelectedAssessor] = useState("");
  const [dialogDisciplines, setDialogDisciplines] = useState([]);
  const [dialogGroups, setDialogGroups] = useState([]);
  const [allocatedAssessors, setAllocatedAssessors] = useState([]);
  const [rejectedAssessors, setRejectedAssessors] = useState([]);
  const [showAvailableAssessors, setShowAvailableAssessors] = useState(false);
  const [disciplineList, setDisciplineList] = useState([]);
  const [datesData, setDatesData] = useState([]);
  const [savingAssessor, setSavingAssessor] = useState(false);
  const [searchingAssessors, setSearchingAssessors] = useState(false);
  const [assessorSearchFilter, setAssessorSearchFilter] = useState("");
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  // State to track selected discipline
  const [selectedDisciplineId, setSelectedDisciplineId] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    assessorId: null,
    discipline: null,
    group: null,
  });
  const [rejectAssessorDialog, setRejectAssessorDialog] = useState({
    open: false,
    assessorId: null,
    assessor: null,
    reason: "",
  });
  const [editAssessorDialog, setEditAssessorDialog] = useState({
    open: false,
    assessor: null,
  });
  const [editingAssessor, setEditingAssessor] = useState(false);
  const [hasIntegratedData, setHasIntegratedData] = useState(false);
  const [assessorType, setAssessorType] = useState("LA"); // 'LA' or 'OTHER'
  const [laToSendAs, setLaToSendAs] = useState(""); // 'LA' or 'LATA' - for when LA_TA is selected
  const [documentReviewDate, setDocumentReviewDate] = useState("");
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    password: "",
    loading: false,
    showPassword: false
  });
  const [rollbackConfirmDialog, setRollbackConfirmDialog] = useState({
    open: false,
    loading: false,
  });
  // Lab details state from API
  const [labDetails, setLabDetails] = useState({
    labName: "",
    Address: "",
    LabType: "",
    Standard: "",
    Stage: "",
    Labid: "",
    CpName: "",
    CpEmail: "",
    CpMobile: "",
    NablDesignation: "",
    NablName: "",
    NablEmail: "",
    NablMobile: "",
  });

  // Available assessors (populated from API)
  const [availableAssessorsList, setAvailableAssessorsList] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cabStage, setCabStage] = useState("");
  const [labTypeId, setLabTypeId] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
      if (appId) {
        loadCABDetails();
      }
    }, [appId]);

    const loadCABDetails = useCallback(async () => {
      if (!appId) {
        setLoading(false);
        return;
      }
  
      try {
        setLoading(true);  
        // Call API to get CAB details (mode 100 equivalent)
        const response = await DoApiService.getCABDetails({
          appId: parseFloat(appId), // Convert to double as per .NET code
        });
        if (response.success) {
          setCabStage(response.data.stage || ""); // Set CAB stage from response
          setLabTypeId(response.data.labTypeId || 0); // Set Lab Type ID from response
        } else {
          throw new Error(response.message || "Failed to load CAB details");
        }
      } catch (err) {
        console.error("Load CAB details error:", err);
      } finally {
        setLoading(false);
      }
    }, [appId]);

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      const response = await getStates('101');
      const statesData = response.data?.data || response.data || [];
      setStates(Array.isArray(statesData) ? statesData : []);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await getCities(stateId);
      const citiesData = response.data?.data || response.data || [];
      setCities(Array.isArray(citiesData) ? citiesData : []);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    }
  };

  // Fetch lab details from API
  useEffect(() => {
    const fetchLabDetails = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/assessor/Getlabdetails?appid=${appId}&orgid=${orgId}`;
        const resp = await axios.get(url);
        const data = resp.data;
        if (data.isstatus) {
          setLabDetails({
            labName: data.labName || "",
            Address: data.Address || "",
            LabType: data.LabType || "",
            Standard: data.Standard || "",
            Stage: data.Stage || "",
            Labid: data.Labid || "",
            CpName: data.CpName || "",
            CpEmail: data.CpEmail || "",
            CpMobile: data.CpMobile || "",
            NablDesignation: data.NablDesignation || "",
            NablName: data.NablName || "",
            NablEmail: data.NablEmail || "",
            NablMobile: data.NablMobile || "",
          });
        } else {
          throw new Error(data.Message || "Failed to fetch lab details");
        }
      } catch (error) {
        console.error("Error fetching lab details:", error);
      }
    };
    fetchLabDetails();
  }, [appId, orgId]);


  // Fetch document review date
  useEffect(() => {
    const fetchDocumentReviewDate = async () => {
      if (!appId || !orgId) return;

      try {
        const url = `${process.env.REACT_APP_API_URL}/assessor/GetDocumentReviewDate?appid=${appId}&orgid=${orgId}`;
        const resp = await axios.get(url);
        const data = resp.data;

        if (data && data.isstatus && data.date) {
          // Format date if needed (assuming it comes in a format we can use)
          setDocumentReviewDate(data.date || "");
        }
      } catch (error) {
        console.error("Error fetching document review date:", error);
      }
    };

    fetchDocumentReviewDate();
  }, [appId, orgId]);

  // Fetch assessment plan (dates and disciplines) from backend when appId is available
  useEffect(() => {
    const fetchAssessmentPlan = async () => {
      if (!appId) return;
      try {
        const url = `${process.env.REACT_APP_API_URL}/assessor/GetAssessmentPlan?appid=${appId}&orgid=${orgId}&Assessmenttype=Main`;
        const resp = await axios.get(url);
        const data = resp.data;

        if (data && data.isstatus) {
          // Map dates
          if (Array.isArray(data.Date) && data.Date.length) {
            const mappedDates = data.Date.map((d, idx) => ({
              id: idx + 1,
              name: `Date ${idx + 1}`,
              from: d.Datefrom || "",
              to: d.Dateto || "",
            }));
            setDatesData(mappedDates);
          }

          // Map disciplines
          if (Array.isArray(data.Dis) && data.Dis.length) {
            const mappedDis = data.Dis.map((d, idx) => ({
              // prefer DisciplineId from API; fall back to index
              id:
                d.DisciplineId ||
                d.DisciplineId ||
                (d.DisciplineId === 0 ? 0 : d.GroupId || idx + 1),
              name:
                d.DisciplineName || d.disciplineName || `Discipline ${idx + 1}`,
              totalscope: d.totalscope || "0",
              Qualitative: d.Qualitative || "0",
              Quantitative: d.Quantitative || "0",
              Calculated: d.Calculated || "0",
              groupName: d.groupName || "",
              groupId: d.GroupId || "",
            }));
            setDisciplineList(mappedDis);

            // Fetch saved assessors for each discipline
            fetchSavedAssessors(mappedDis);
          }

          // Bind IntegratedData if present
          if (data.IntegratedData) {
            const idata = data.IntegratedData;
            if (idata) {
              setHasIntegratedData(true);
            }
            // local helpers to safely convert backend date strings to YYYY-MM-DD for input[type=date]
            const formatToISOInput = (dateStr) => {
              if (!dateStr) return "";
              const s = String(dateStr).trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
              if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
                const parts = s.split("/"); // DD/MM/YYYY
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
              }
              if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
                const parts = s.split("-");
                // if first part 4 chars assume YYYY-MM-DD
                if (parts[0].length === 4) return s;
                // MM-DD-YYYY -> YYYY-MM-DD
                return `${parts[2]}-${parts[0]}-${parts[1]}`;
              }
              const parsed = new Date(s);
              if (!isNaN(parsed.getTime()))
                return parsed.toISOString().split("T")[0];
              return "";
            };

            const addDaysLocal = (isoDate, days) => {
              if (!isoDate) return "";
              const d = new Date(isoDate);
              if (isNaN(d.getTime())) return "";
              d.setDate(d.getDate() + days);
              return d.toISOString().split("T")[0];
            };

            const integratedFlag = idata.isintegrated;
            setIsIntegratedAssessment(
              integratedFlag === 1 || integratedFlag === "1" ? "Yes" : "No"
            );

            const sDate = formatToISOInput(idata.startdate);
            const eDate = formatToISOInput(idata.enddate);
            setStartDate(sDate || "");
            if (eDate) {
              setEndDate(eDate);
            } else if (sDate) {
              if (integratedFlag === 1 || integratedFlag === "1")
                setEndDate(addDaysLocal(sDate, 4));
              else setEndDate(addDaysLocal(sDate, 1));
            }

            const moreSlotFlag = idata.MoreSlot;
            setOneMoreSlot(
              moreSlotFlag === 1 || moreSlotFlag === "1" ? "Yes" : "No"
            );

            const slotS = formatToISOInput(idata.SlotStartDate);
            const slotE = formatToISOInput(idata.SlotEndDate);
            setAdditionalStartDate(slotS || "");
            if (slotE) setAdditionalEndDate(slotE);
            else if (slotS) setAdditionalEndDate(addDaysLocal(slotS, 1));

            setReason(idata.Remarks || idata.Remark || "");
          }
        } else {
          console.error("GetAssessmentPlan returned error or empty:", data);
          showSnackbar(
            "Failed to load assessment plan, using defaults",
            "error"
          );
        }
      } catch (err) {
        console.error("Error fetching assessment plan:", err);
        showSnackbar(
          "Unable to fetch assessment plan; using defaults",
          "error"
        );
      }
    };

    fetchAssessmentPlan();
  }, [appId]);


  // Comprehensive refresh function to reset all state after rollback
  const refreshAllDataAndResetUI = async () => {
    try {
      // Close all dialogs
      setOpenAssessorDialog(false);
      setOpenCabDialog(false);
      loadCABDetails();
      setCabStage(getCabStage()); // Reset cabStage to current value
      setDeleteConfirmDialog({ open: false, assessorId: null, discipline: null, group: null });
      setRejectAssessorDialog({ open: false, assessorId: null, assessor: null, reason: "" });
      setEditAssessorDialog({ open: false, assessor: null });
      setPasswordDialog({ open: false, password: "", loading: false, showPassword: false });

      // Reset dialog state variables
      setDialogCapacity("");
      setDialogStartDate("");
      setDialogEndDate("");
      setDialogSelectedAssessor("");
      setDialogDisciplines([]);

      // Clear available assessors and hide panel
      setAvailableAssessorsList([]);
      setShowAvailableAssessors(false);

      // Reset search filters
      setAssessorSearchFilter("");
      setStateFilter(null);
      setCityFilter(null);
      setSelectedDisciplineId(null);
      setSelectedGroupId(null);
      // Reset loading states
      setSavingAssessor(false);
      setSearchingAssessors(false);
      setEditingAssessor(false);

      // Reset form fields
      setAssessorType("LA");
      setLaToSendAs("");

      // Refresh all assessor data
      fetchRejectedAssessors(); // Refresh rejected assessors list
      fetchDeactivatedAssessors(); // Refresh deactivated assessors list
      fetchSavedAssessors(disciplineList); // Refresh allocated assessors list
      
      // Re-fetch assessment plan to ensure discipline list is up to date
      if (appId && orgId) {
        const url = `${process.env.REACT_APP_API_URL}/assessor/GetAssessmentPlan?appid=${appId}&orgid=${orgId}&Assessmenttype=Main`;
        const resp = await axios.get(url);
        const data = resp.data;
        
        if (data && data.isstatus && Array.isArray(data.Dis) && data.Dis.length) {
          const mappedDis = data.Dis.map((d, idx) => ({
            id: d.DisciplineId || d.DisciplineId || (d.DisciplineId === 0 ? 0 : d.GroupId || idx + 1),
            name: d.DisciplineName || d.disciplineName || `Discipline ${idx + 1}`,
            totalscope: d.totalscope || "0",
            Qualitative: d.Qualitative || "0",
            Quantitative: d.Quantitative || "0",
            Calculated: d.Calculated || "0",
          }));
          setDisciplineList(mappedDis);
        }
      }

    } catch (error) {
      console.error('❌ Error during comprehensive refresh:', error);
    }
  };

  const handleRollbackAssessment = async () => {
    // Open confirmation dialog instead of running rollback directly
    setRollbackConfirmDialog({ open: true, loading: false });
  };

  const confirmRollbackAssessment = async () => {
    if (!appId || !orgId) return;
    
    setRollbackConfirmDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const url = `${process.env.REACT_APP_API_URL}/do/rollback/${appId}`;
      const response = await axios.put(url);
      if (response.data.success) {
        showSnackbar("Rollback successful", "success");
        // Perform comprehensive refresh and UI reset
        await refreshAllDataAndResetUI();
        setRollbackConfirmDialog({ open: false, loading: false });
      } else {
        console.error('❌ Rollback failed:', response.data);
        showSnackbar("Rollback failed", "error");
        setRollbackConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('❌ Error during rollback:', error);
      showSnackbar("Error during rollback", "error");
      setRollbackConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Function to fetch saved assessors for all disciplines
  const fetchSavedAssessors = async (disciplines) => {
    if (!appId || !disciplines || disciplines.length === 0) return;

    // First, deactivate CAB rejected assessors
    try {
      const deactivateResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/do/deactivate-cab-rejected-assessors`,
        { appId: parseInt(appId) }
      );

      if (deactivateResponse.data.success) {
        console.log('✅ Deactivate result:', deactivateResponse.data);
        if (deactivateResponse.data.data.updatedCount > 0) {
          console.log(`✅ Deactivated ${deactivateResponse.data.data.updatedCount} CAB rejected assessor(s)`);
        }
      }
    } catch (deactivateError) {
      console.error('❌ Error deactivating CAB rejected assessors:', deactivateError);
      // Don't stop the flow, continue fetching assessors
    }

    try {
      const allAssessors = [];
      const addedAssessorIds = new Set(); // Track already added assessors to prevent duplicates

      // Fetch rejected assessors using dedicated API
      fetchRejectedAssessors();
      const response = await DoApiService.getCABDetails({appId: parseFloat(appId)});

      const labTypeId = response.success ? response.data.labTypeId || 0 : 0;
      // Fetch assessors for each discipline
      if (labTypeId === 1 || labTypeId === 2) {
        for (const discipline of disciplines) {
          try {
            const url = `${process.env.REACT_APP_API_URL}/assessor/Getassessor?appid=${appId}&orgid=${orgId}&disciplineId=${discipline.id}`;
            const resp = await axios.get(url);
            const data = resp.data;
            if (data && data.isstatus && Array.isArray(data.assessor)) {
              const mappedAssessors = data.assessor
                .filter((a) => {
                  if (a.CabAccepted === 2 || a.AssessorAccepted === 2)
                    return false;
                  const hasCapacity = a.Capicity && a.Capicity !== "";
                  const hasDiscipline =
                    a.Discipline &&
                    a.Discipline !== "" &&
                    a.Discipline !== null &&
                    a.Discipline !== "0";
                  const hasGroup =
                    a.Group &&
                    a.Group !== "" &&
                    a.Group !== null &&
                    a.Group !== "0";
                  // Discipline and Group IDs from assessor
                  const disciplineIds = hasDiscipline
                    ? String(a.Discipline)
                        .split(",")
                        .map((id) => id.trim())
                    : [];
                  const groupIds = hasGroup
                    ? String(a.Group)
                        .split(",")
                        .map((id) => id.trim())
                    : [];
                  // Discipline and Group IDs from current discipline object
                  const disciplineId = String(discipline.id);
                  const groupId = String(discipline.groupId);
                  // Check if both discipline and group match
                  const isDisciplineAndGroupMatched =
                    disciplineIds.includes(disciplineId) &&
                    groupIds.includes(groupId);
                  // LA_TA
                  if (hasCapacity && a.Capicity === "LA_TA") {
                    if (hasDiscipline && hasGroup)
                      return isDisciplineAndGroupMatched;
                    return true;
                  }
                  if (addedAssessorIds.has(a.id)) return false;
                  if (hasCapacity && a.Capicity === "LA") {
                    return !hasDiscipline && !hasGroup;
                  }
                  if (
                    hasCapacity &&
                    ["TA", "TE", "OBS", "Observer"].includes(a.Capicity)
                  ) {
                    if (hasDiscipline && hasGroup)
                      return isDisciplineAndGroupMatched;
                    if ((a.Capicity === "OBS" || a.Capicity === "Observer") && !hasDiscipline && !hasGroup) {
                      if (addedAssessorIds.has(a.id)) return false;
                      addedAssessorIds.add(a.id);
                      return true;
                    }
                    return false;
                  }
                  if (!hasCapacity && hasDiscipline && hasGroup) {
                    return isDisciplineAndGroupMatched;
                  }
                  return false;
                })
                .map((a) => {
                  if (
                    a.Capicity === "LA" &&
                    (!a.Discipline ||
                      a.Discipline === "" ||
                      a.Discipline === null ||
                      a.Discipline === "0")
                  ) {
                    addedAssessorIds.add(a.id);
                  }
                  return {
                    id: a.id || Date.now() + Math.random(),
                    discipline: discipline.name,
                    disciplines: [discipline.name],
                    disciplineIds: [discipline.id],
                    groupIds: [discipline.groupId],
                    capacity:
                      a.Capicity === "Observer" ? "OBS" : a.Capicity || "",
                    startDate: a.datefrom
                      ? a.datefrom.includes("T")
                        ? a.datefrom.split("T")[0]
                        : a.datefrom.split(" ")[0]
                      : "",
                    endDate: a.dateto
                      ? a.dateto.includes("T")
                        ? a.dateto.split("T")[0]
                        : a.dateto.split(" ")[0]
                      : "",
                    assessor: a.AssessorName || "",
                    assessorId: a.Assessorid || "",
                    qualification: a.Discipline || "",
                    experience: "",
                    Discipline: a.Discipline ? String(a.Discipline) : "",
                    Group: a.Group ? String(a.Group) : "",
                  };
                });
              allAssessors.push(...mappedAssessors);
            }
          } catch (err) {
            console.error(
              `Error fetching assessors for discipline ${discipline.name}:`,
              err,
            );
          }
        }
      } else {
        for (const discipline of disciplines) {
          try {
            const url = `${process.env.REACT_APP_API_URL}/assessor/Getassessor?appid=${appId}&orgid=${orgId}&disciplineId=${discipline.id}`;
            const resp = await axios.get(url);
            const data = resp.data;

            if (data && data.isstatus && Array.isArray(data.assessor)) {
              // Map the response to match local allocation format (exclude rejected)
              const mappedAssessors = data.assessor
                .filter((a) => {
                  // Exclude rejected assessors (CabAccepted=2 or AssessorAccepted=2)
                  if (a.CabAccepted === 2 || a.AssessorAccepted === 2)
                    return false;

                  const hasCapacity = a.Capicity && a.Capicity !== "";
                  const hasDiscipline =
                    a.Discipline &&
                    a.Discipline !== "" &&
                    a.Discipline !== null &&
                    a.Discipline !== "0";

                  // For LA_TA - always allow in both tables (no duplicate check)
                  if (hasCapacity && a.Capicity === "LA_TA") {
                    // If LA_TA has discipline, check if current discipline is included
                    if (hasDiscipline) {
                      // Handle comma-separated disciplines (e.g., "1,3,5")
                      const disciplineIds = String(a.Discipline)
                        .split(",")
                        .map((id) => id.trim());
                      return disciplineIds.includes(String(discipline.id));
                    }
                    // If LA_TA has no discipline, include it in all discipline sections for Other Team table
                    return true;
                  }

                  // Skip if already added (prevents duplicates for LA and other capacities)
                  if (addedAssessorIds.has(a.id)) return false;

                  // For LA - include only if Discipline field is null/blank/empty
                  if (hasCapacity && a.Capicity === "LA") {
                    return !hasDiscipline;
                  }

                  // For TA, TE, OBS/Observer - include if Discipline field matches current discipline ID
                  if (
                    hasCapacity &&
                    (a.Capicity === "TA" ||
                      a.Capicity === "TE" ||
                      a.Capicity === "OBS" ||
                      a.Capicity === "Observer")
                  ) {
                    if (hasDiscipline) {
                      // Handle comma-separated disciplines (e.g., "1,3,5")
                      const disciplineIds = String(a.Discipline)
                        .split(",")
                        .map((id) => id.trim());
                      return disciplineIds.includes(String(discipline.id));
                    }
                    if ((a.Capicity === "OBS" || a.Capicity === "Observer") && !hasDiscipline) {
                      if (addedAssessorIds.has(a.id)) return false;
                      addedAssessorIds.add(a.id);
                      return true;
                    }
                    return false;
                  }

                  // If no capacity but has discipline ID matching current discipline - include in Other Team Members
                  if (!hasCapacity && hasDiscipline) {
                    // Handle comma-separated disciplines (e.g., "1,3,5")
                    const disciplineIds = String(a.Discipline)
                      .split(",")
                      .map((id) => id.trim());
                    return disciplineIds.includes(String(discipline.id));
                  }

                  return false;
                })
                .map((a) => {
                  // Only add to tracking Set if LA (not LA_TA) and has null/empty discipline
                  // LA_TA is never added to tracking set - allowing it to appear in both tables
                  if (
                    a.Capicity === "LA" &&
                    (!a.Discipline ||
                      a.Discipline === "" ||
                      a.Discipline === null ||
                      a.Discipline === "0")
                  ) {
                    addedAssessorIds.add(a.id);
                  }
                  // Note: LA_TA is intentionally NOT added to tracking set to allow duplicates in both tables
                  return {
                    id: a.id || Date.now() + Math.random(),
                    discipline: discipline.name,
                    disciplines: [discipline.name],
                    disciplineIds: [discipline.id],
                    capacity:
                      a.Capicity === "Observer" ? "OBS" : a.Capicity || "",
                    startDate: a.datefrom
                      ? a.datefrom.includes("T")
                        ? a.datefrom.split("T")[0]
                        : a.datefrom.split(" ")[0]
                      : "",
                    endDate: a.dateto
                      ? a.dateto.includes("T")
                        ? a.dateto.split("T")[0]
                        : a.dateto.split(" ")[0]
                      : "",
                    assessor: a.AssessorName || "",
                    assessorId: a.Assessorid || "",
                    qualification: a.Discipline || "",
                    experience: "",
                    // Preserve original Disciplsine field from API (not the loop discipline)
                    Discipline: a.Discipline ? String(a.Discipline) : "",
                    Group: a.Group ? String(a.Group) : "",
                  };
                });
              allAssessors.push(...mappedAssessors);
            }
          } catch (err) {
            console.error(
              `Error fetching assessors for discipline ${discipline.name}:`,
              err,
            );
          }
        }
      }
      // Fetch saved observers (OBS without discipline) via dedicated endpoint
      try {
        const obsUrl = `${process.env.REACT_APP_API_URL}/assessor/GetSavedObservers?appid=${appId}&orgid=${orgId}`;
        const obsResp = await axios.get(obsUrl);
        const obsData = obsResp.data;
        if (obsData && obsData.isstatus && Array.isArray(obsData.assessor) && obsData.assessor.length > 0) {
          obsData.assessor
            .filter(a => a.CabAccepted !== 2 && a.AssessorAccepted !== 2)
            .forEach(a => {
              const id = a.id || Date.now() + Math.random();
              // prevent duplicates (in case discipline loop already picked them up)
              if (!allAssessors.some(existing => existing.id === id)) {
                allAssessors.push({
                  id,
                  discipline: "",
                  disciplines: [],
                  disciplineIds: [],
                  groupIds: [],
                  capacity: "OBS",
                  startDate: a.datefrom
                    ? a.datefrom.includes("T") ? a.datefrom.split("T")[0] : a.datefrom.split(" ")[0]
                    : "",
                  endDate: a.dateto
                    ? a.dateto.includes("T") ? a.dateto.split("T")[0] : a.dateto.split(" ")[0]
                    : "",
                  assessor: a.AssessorName || "",
                  assessorId: a.Assessorid || "",
                  qualification: "",
                  experience: "",
                  Discipline: "",
                  Group: "",
                });
              }
            });
        }
      } catch (obsErr) {
        console.error("Error fetching saved observers:", obsErr);
      }

      setAllocatedAssessors(allAssessors);
    } catch (err) {
      console.error("Error fetching saved assessors:", err);
    }
  };

  // Function to fetch rejected assessors
  const fetchRejectedAssessors = async () => {
    if (!appId) return;

    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor/GetRejectedAssessor?appid=${appId}`;
      const resp = await axios.get(url);
      const data = resp.data;

      if (data && data.isstatus && Array.isArray(data.assessor)) {
        const rejectedList = data.assessor.map((a) => ({
          id: a.id || Date.now() + Math.random(),
          discipline: a.DisciplineName || '-',
          capacity: a.Capicity === "Observer" ? "OBS" : a.Capicity || "",
          startDate: a.datefrom
            ? a.datefrom.includes("T")
              ? a.datefrom.split("T")[0]
              : a.datefrom.split(" ")[0]
            : "",
          endDate: a.dateto
            ? a.dateto.includes("T")
              ? a.dateto.split("T")[0]
              : a.dateto.split(" ")[0]
            : "",
          assessor: a.AssessorName || "",
          assessorId: a.Assessorid || "",
          rejectedDate: a.RejectedDate || "",
          CabAcceptDate: a.CabAcceptDate || "",
          AssessorAcceptDate: a.AssessorAcceptDate || "",
          DoRejectDate: a.Do_rejectDate || "",
          rejectedBy: a.CabAccepted === 2 ? 'CAB' : a.JDirectorAccepted === 2 ? 'Joint Director' : a.Do_reject ? 'DO' : 'Assessor',
          remarks: a.CabAccepted === 2 ? (a.CABDenyReason || '-') : a.AssessorAccepted === 2 ? (a.AssessorDenyReason || '-') : a.JDirectorAccepted === 2 ? (a.JDirectorDenyReason || '-') : a.Do_reject ? (a.Do_rejectReason || '-') : '-',
        }));
        setRejectedAssessors(rejectedList);
      } else {
        setRejectedAssessors([]);
      }
    } catch (err) {
      console.error("Error fetching rejected assessors:", err);
      setRejectedAssessors([]);
    }
  };

  const fetchDeactivatedAssessors = async () => {
    if (!appId) return [];
    try {
      const url = `${process.env.REACT_APP_API_URL}/do/GetDeactivatedAssessors?appid=${appId}`;
      const resp = await axios.get(url);
      const data = resp.data;

      if (data?.isstatus && Array.isArray(data.assessor)) {
        const deactivatedList = data.assessor.map(a => ({
          id: a.id ?? `${Date.now()}-${Math.random()}`,
          name: a.AssessorName || "",
        }));
        return deactivatedList;
      }

    } catch (error) {
      console.error("Error fetching deactivated assessors:", error);
      return [];
    }
  };

  // Function to map lab type number to string
  const getLabTypeString = (labType) => {
    const labTypeMap = {
      1: "Testing",
      2: "Calibration",
      3: "Medical",
    };
    return labTypeMap[labType] || labType;
  };

  // Function to handle opening assessor dialog
  const handleOpenAssessorDialog = (disciplineId, groupId, type = "LA") => {
    setOpenAssessorDialog(true);
    setShowAvailableAssessors(false);
    setDialogSelectedAssessor("");
    setDialogCapacity("");
    setLaToSendAs(""); // Reset the LA send as selection
    setDialogStartDate(startDate);
    setDialogEndDate(endDate);
    setSelectedDisciplineId(disciplineId);
    setSelectedGroupId(groupId);
    setAssessorType(type);
    setAssessorSearchFilter("");
    setStateFilter(null);
    setCityFilter(null);
    // Pre-select the discipline in dialog (for convenience)
    if (disciplineId) setDialogDisciplines([disciplineId]);
    if (groupId) setDialogGroups([groupId]);
  };

  // Function to check available assessors
  const handleCheckAvailableAssessors = async () => {
    if (!dialogCapacity || !dialogStartDate || !dialogEndDate) {
      showSnackbar("Please select capacity and date range first", "error");
      return;
    }

    // Check if LA_TA is selected and laToSendAs is not set
    if (dialogCapacity === "LA_TA" && !laToSendAs) {
      showSnackbar("Please specify whether to send the assessor as LA or LA+TA", "error");
      return;
    }

    // Determine discipline id to query (if user selected multiple, use first for availability check)
    const disciplineId =
      dialogDisciplines && dialogDisciplines.length
        ? dialogDisciplines[0]
        : selectedDisciplineId;

    // Determine group id to query (if user selected multiple, use first for availability check)
    const groupId =
      dialogGroups && dialogGroups.length
        ? dialogGroups[0]
        : selectedGroupId;    

    if (assessorType === "OTHER") {
      if (!disciplineId) {
        showSnackbar("Please select a discipline", "error");
        return;
      }
    }

    setSearchingAssessors(true);
    try {
      // Prepare query params; backend expects datetime strings like 'YYYY-MM-DD 00:00:00'
      const datefrom = `${dialogStartDate} 00:00:00`;
      const dateto = `${dialogEndDate} 00:00:00`;

      const url = `${process.env.REACT_APP_API_URL}/assessor/GetassessorName`;
      const resp = await axios.get(url, {
        params: {
          DisciplineId: assessorType === "OTHER" ? disciplineId : assessorType === "OBS" ? "" : "null",
          GroupId: assessorType === "OTHER" ? groupId : assessorType === "OBS" ? "" : "null",
          Capacity: dialogCapacity,
          datefrom,
          dateto,
          appid: appId,
          orgid: orgId,
          labType: labDetails.LabType || "",
        },
      });

      const data = resp.data;
      if (data && data.isstatus && Array.isArray(data.assessor)) {
        const mapped = data.assessor.map((a) => ({
          id: Number(a.Assessorid),
          name: a.AssessorName,
          available: true,
          disciplines: a.MatchingDiscipline || "",
          disciplineIds: a.DisciplineId || "",
          groupIds: a.GroupIds || "",
          groupNames: a.groupNames || "",
          matchingGroup: a.matchingGroup || "",
          Capacity: a.Capacity || "",
          address: a.address || "",
        }));
        setAvailableAssessorsList(mapped);
        setShowAvailableAssessors(true);
      } else {
        setAvailableAssessorsList([]);
        setShowAvailableAssessors(false);
        showSnackbar(
          data.Message || "No assessors found for selected criteria",
          "error"
        );
      }
    } catch (err) {
      console.error("Error fetching assessors:", err);
      showSnackbar("Unable to fetch assessors. Please try again.", "error");
      setAvailableAssessorsList([]);
      setShowAvailableAssessors(false);
    } finally {
      setSearchingAssessors(false);
    }
  };

  // Helper function to check if LA or LA_TA already exists
  const checkExistingCapacity = (capacityType) => {
    if (capacityType === "LA" || capacityType === "LA_TA") {
      return allocatedAssessors.some(
        (assessor) => assessor.capacity === "LA" || assessor.capacity === "LA_TA"
      );
    }
    return allocatedAssessors.some(
      (assessor) => assessor.capacity === capacityType
    );
  };

  // Function to validate capacity selection
  const validateCapacitySelection = (capacity) => {
    if ((capacity === "LA" || capacity === "LA_TA") && checkExistingCapacity(capacity)) {
      showSnackbar("A Lead Assessor (LA or LA_TA) has already been allocated.", "error");
      return false;
    }
    return true;
  };

  const handleCloseAssessorDialog = () => {
    setOpenAssessorDialog(false);
    setDialogCapacity("");
    setLaToSendAs(""); // Reset the LA send as selection
    setDialogStartDate("");
    setDialogEndDate("");
    setDialogSelectedAssessor("");
    setDialogDisciplines([]);
    setShowAvailableAssessors(false);
  };


  // Function to handle saving assessor allocation
  const handleSaveAssessor = async () => {
    if (
      !dialogCapacity ||
      !dialogStartDate ||
      !dialogEndDate ||
      !dialogSelectedAssessor
    ) {
      showSnackbar(
        "Please fill all required fields including selecting an assessor",
        "error"
      );
      return;
    }

    // Check if LA_TA is selected and laToSendAs is not set
    if (dialogCapacity === "LA_TA" && !laToSendAs) {
      showSnackbar("Please specify whether to send the assessor as LA or LA+TA", "error");
      return;
    }

    setSavingAssessor(true);

    try {
      const selectedAssessorData = availableAssessorsList.find(
        (assessor) => assessor.id === dialogSelectedAssessor
      );

      const selectedDiscipline = disciplineList.find(
        (d) => d.id === selectedDisciplineId
      );

      const selectedDisciplines =
        dialogDisciplines && dialogDisciplines.length
          ? disciplineList
            .filter((d) => dialogDisciplines.includes(d.id))
            .map((d) => d.name)
          : selectedDiscipline
            ? [selectedDiscipline.name]
            : [];

      // Build payload matching backend SaveAssessor Request shape
      // Convert dialogDisciplines array to comma-separated string
      const disciplineIdForApi =
        dialogDisciplines && dialogDisciplines.length
          ? dialogDisciplines.join(",")
          : selectedDisciplineId
            ? String(selectedDisciplineId)
            : "";

      // const groupIdForApi = dialogGroups && dialogGroups.length
      //     ? dialogGroups.map((v) => String(v.GroupId || v.groupId)).join(",")
      //     : selectedGroupId
      //       ? String(selectedGroupId)
      //       : "";   

      const groupIdForApi = dialogGroups && dialogGroups.length ? (
        typeof dialogGroups[0] === "object"
          ? dialogGroups
              .map((v) =>
                String(
                  v.GroupId ||
                  v.groupId,
                ),
              )
              .join(",")
          : dialogGroups.join(",")
      ): selectedGroupId
      ? String(selectedGroupId)
      : "";   

      if (assessorType === "OTHER") {
        if (!disciplineIdForApi) {
          showSnackbar("Discipline ID is missing. Please try again.", "error");
          setSavingAssessor(false);
          return;
        }
      }

      // Determine the actual capacity to send based on user selection for LA_TA
      const actualCapacityToSend = dialogCapacity === "LA_TA" && laToSendAs === "LA" ? "LA" : dialogCapacity;

      const payload = {
        AppId: appId || "",
        AssessmentType: "Main",
        ClientIPCreate: "0",
        Stage: labDetails.Stage || "",
        AllotedToId: String(dialogSelectedAssessor),
        LoginTypeId: "6",
        DateFrom: dialogStartDate ? `${dialogStartDate} 00:00:00` : "",
        DateTo: dialogEndDate ? `${dialogEndDate} 00:00:00` : "",
        ExpertiseField: selectedAssessorData?.qualification || "",
        DisciplineId: assessorType === "OTHER" ? disciplineIdForApi : actualCapacityToSend === "LA_TA" ? disciplineIdForApi : "",
        GroupId: assessorType === "OTHER" ? groupIdForApi : actualCapacityToSend === "LA_TA" ? groupIdForApi : "",
        IsLA: actualCapacityToSend === "LA" ? 1 : 0,
        IsTL: actualCapacityToSend === "TL" ? 1 : 0,
        IsLA_TA: actualCapacityToSend === "LA_TA" ? 1 : 0,
        isTE: actualCapacityToSend === "TE" ? 1 : 0,
        isTA: actualCapacityToSend === "TA" ? 1 : 0,
        isObserver: actualCapacityToSend === "OBS" ? 1 : 0,
        ReAllotment: 0,
        location: "",
        AssessmentMode: "App",
        AllotedById: localStorage.getItem("orgId") || "0",
        ClientIP: "0",
        // Auto-accept for stage 20
        CabAccepted: labDetails.Stage === "20" ? 1 : undefined,
        JDirectorAccepted: labDetails.Stage === "20" ? 1 : undefined,
        AssessorAccepted: labDetails.Stage === "20" ? 1 : undefined,
      };
      const url = `${process.env.REACT_APP_API_URL}/assessor/Saveassessor`;
      const resp = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = resp.data;

      if (data && data.isstatus) {
        fetchSavedAssessors(disciplineList);
        showSnackbar(data.Message || "Assessor saved", "success");
        // Close dialog and reset form
        setOpenAssessorDialog(false);
        setDialogCapacity("");
        setDialogStartDate("");
        setDialogEndDate("");
        setDialogSelectedAssessor("");
        setDialogDisciplines([]);
        setShowAvailableAssessors(false);
      } else {
        console.error("Saveassessor returned error:", data);
        showSnackbar(data.Message || "Failed to save assessor", "error");
      }
    } catch (err) {
      console.error("Error saving assessor:", err.message);
      showSnackbar("Unable to save assessor. Please try again.", "error");
    } finally {
      setSavingAssessor(false);
    }
  };

  // Function to handle deleting an allocated assessor
  const handleDeleteAllocation = (id, disciplineIdOrFull, groupIdOrFull, currentDisciplineContext = null, currentGroupContext = null) => {
    let disciplineId = disciplineIdOrFull;
    let groupId = groupIdOrFull;


    // If we have a specific discipline context (from discipline loop), use it
    if (currentDisciplineContext) {
      disciplineId = currentDisciplineContext;
    }
    // If disciplineIdOrFull contains comma-separated values, extract the first one
    else if (disciplineIdOrFull && typeof disciplineIdOrFull === 'string' && disciplineIdOrFull.includes(',')) {
      // Split comma-separated disciplines and take the first one
      const disciplines = disciplineIdOrFull.split(',').map(d => d.trim()).filter(d => d);
      disciplineId = disciplines[0] || disciplineIdOrFull;
    }

    // If we have a specific group context (from group loop), use it
    if (currentGroupContext) {
      groupId = currentGroupContext;
    }
    // If groupIdOrFull contains comma-separated values, extract the first one
    else if (groupIdOrFull && typeof groupIdOrFull === 'string' && groupIdOrFull.includes(',')) {
      // Split comma-separated groups and take the first one
      const groups = groupIdOrFull.split(',').map(g => g.trim()).filter(g => g);
      groupId = groups[0] || groupIdOrFull;
    }

    setDeleteConfirmDialog({
      open: true,
      assessorId: id,
      discipline: disciplineId,
      group: groupId
    });
  };

  const confirmDeleteAssessor = async () => {
    const id = deleteConfirmDialog.assessorId;
    const disciplineId = deleteConfirmDialog.discipline;
    const groupId = deleteConfirmDialog.group;
    setDeleteConfirmDialog({ open: false, assessorId: null, discipline: null, group: null });

    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor/Deleteassessor`;
      const resp = await axios.delete(url, {
        data: { id: id, disciplineId: disciplineId, groupId: groupId },
      });
      const data = resp.data;
      if (data && data.isstatus) {
        setAllocatedAssessors((prevAssessors) =>
          prevAssessors.filter((allocation) => allocation.id !== id)
        );
        showSnackbar(
          data.Message || "Assessor deleted successfully",
          "success"
        );
        window.location.reload(); // Refresh the page to reflect changes
      } else {
        showSnackbar(data.Message || "Failed to delete assessor", "error");
      }
    } catch (err) {
      console.error("Error deleting assessor:", err);
      showSnackbar("Unable to delete assessor. Please try again.", "error");
    }
  };

  // Function to handle rejecting an assessor (for stage 20)
  const handleRejectAssessor = (assessor) => {
    setRejectAssessorDialog({
      open: true,
      assessorId: assessor.id,
      assessor: assessor,
      reason: "",
    });
  };

  // Function to confirm reject assessor
  const confirmRejectAssessor = async () => {
    const { assessorId, reason } = rejectAssessorDialog;

    if (!reason.trim()) {
      showSnackbar("Please provide a reason for rejection", "error");
      return;
    }

    setRejectAssessorDialog({ open: false, assessorId: null, assessor: null, reason: "" });

    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor/RejectAssessor`;
      const payload = {
        id: assessorId,
        reason: reason.trim(),
        appId: appId,
        rejectedById: localStorage.getItem("orgId") || "0"
      };

      const resp = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = resp.data;
      if (data && data.isstatus) {
        // Remove from allocated assessors
        setAllocatedAssessors((prevAssessors) =>
          prevAssessors.filter((allocation) => allocation.id !== assessorId)
        );
        showSnackbar(
          data.Message || "Assessor rejected successfully",
          "success"
        );
        // Refresh rejected assessors list
        fetchRejectedAssessors();
      } else {
        showSnackbar(data.Message || "Failed to reject assessor", "error");
      }
    } catch (err) {
      console.error("Error rejecting assessor:", err);
      showSnackbar("Unable to reject assessor. Please try again.", "error");
    }
  };

  // Function to handle editing an assessor
  const handleEditAssessor = (assessor) => {
    setEditAssessorDialog({
      open: true,
      assessor: { ...assessor },
    });
  };

  // Function to update assessor
  const handleUpdateAssessor = async () => {
    const assessor = editAssessorDialog.assessor;
    if (!assessor) return;

    setEditingAssessor(true);
    try {
      // Convert disciplineIds array to comma-separated string
      const disciplineIds =
        assessor.disciplineIds && assessor.disciplineIds.length
          ? assessor.disciplineIds.join(",")
          : "";

      const payload = {
        id: assessor.id,
        AllotedToId: String(assessor.assessorId),
        DateFrom: assessor.startDate
          ? `${assessor.startDate.split("T")[0]} 00:00:00`
          : "",
        DateTo: assessor.endDate
          ? `${assessor.endDate.split("T")[0]} 00:00:00`
          : "",
        DisciplineId: disciplineIds,
        IsLA: assessor.capacity === "LA" ? 1 : 0,
        IsTL: assessor.capacity === "TL" ? 1 : 0,
        IsLA_TA: assessor.capacity === "LA_TA" ? 1 : 0,
        isTE: assessor.capacity === "TE" ? 1 : 0,
        isTA: assessor.capacity === "TA" ? 1 : 0,
        isObserver: assessor.capacity === "OBS" ? 1 : 0,
      };

      const url = `${process.env.REACT_APP_API_URL}/assessor/UpdateAssessor`;
      const resp = await axios.put(url, payload);
      const data = resp.data;

      if (data && data.isstatus) {
        showSnackbar("Assessor updated successfully", "success");
        setEditAssessorDialog({ open: false, assessor: null });
        await fetchSavedAssessors(disciplineList);
      } else {
        showSnackbar(data.Message || "Failed to update assessor", "error");
      }
    } catch (err) {
      console.error("Error updating assessor:", err);
      showSnackbar("Unable to update assessor. Please try again.", "error");
    } finally {
      setEditingAssessor(false);
    }
  };

  // datesData is managed by state and can be fetched from API (see useEffect below)

  const handleIntegratedAssessmentChange = (event) => {
    setIsIntegratedAssessment(event.target.value);
    // Reset dates when changing assessment type
    setStartDate("");
    setEndDate("");
  };

  // Get minimum date (maximum of: documentReviewDate + 15 days OR 22 December 2025)
  const getMinDate = () => {
    const fixedDate = new Date('2025-12-22'); // 22 December 2025

    if (documentReviewDate) {
      const reviewDate = new Date(documentReviewDate);
      // Add 15 days to the review date
      reviewDate.setDate(reviewDate.getDate() + 0);

      // Return the later date between reviewDate+15days and 22 Dec 2025
      const maxDate = reviewDate > fixedDate ? reviewDate : fixedDate;
      return maxDate.toISOString().split('T')[0];
    }

    // If no document review date, return 22 Dec 2025
    return fixedDate.toISOString().split('T')[0];
  };

  // Helper function to get next day's date
  const getNextDay = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.toISOString().split("T")[0];
  };

  // Helper function to get next day's date
  const get4NextDay = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 4);
    return nextDay.toISOString().split("T")[0];
  };

  // Handle start date change
  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    setStartDate(selectedDate);

    // If not integrated assessment, automatically set end date to next day
    if (isIntegratedAssessment === "No" && selectedDate) {
      setEndDate(getNextDay(selectedDate));
      // when primary start changes, clear any additional slot dates
      setAdditionalStartDate("");
      setAdditionalEndDate("");
    } else if (isIntegratedAssessment === "Yes" && selectedDate) {
      setEndDate(get4NextDay(selectedDate));
      // for integrated assessments, also clear additional slot dates when start changes
      setAdditionalStartDate("");
      setAdditionalEndDate("");
    }
  };

  const handleViewProfile = (assessorId) => {
    window.open(
      `/main/assessor/assessor-profile?AssessorId=${encryptText(assessorId)}`,
      "_blank"
    );
  };

  const handleSubmitAssessmentPlan = async () => {
    // Validate basic requirements
    if (!startDate) {
      showSnackbar("Please select start date", "error");
      return;
    }

    try {
      const payload = {
        appid: appId || "",
        orgid: orgId || "",
        isintegrated: isIntegratedAssessment === "Yes" ? 1 : 0,
        Datefrom: startDate ? `${startDate}` : "",
        Takemoreslot: oneMoreSlot === "Yes" ? 1 : 0,
        MoreslotDatefrom: additionalStartDate ? `${additionalStartDate}` : "",
        Reason: reason || "",
        insert_By: localStorage.getItem("orgId") || "0",
        AssessmentType: "Main",
      };

      const url = `${process.env.REACT_APP_API_URL}/assessor/SetIntegrated`;
      const resp = await axios.post(url, payload);
      const data = resp.data;

      if (data && data.isstatus) {
        showSnackbar(data.Message || "Assessment plan updated", "success");
      } else {
        console.error("SetIntegrated returned error:", data);
        showSnackbar(
          data.Message || "Failed to update assessment plan",
          "error"
        );
      }
    } catch (err) {
      console.error("Error calling SetIntegrated:", err);
      showSnackbar(
        "Unable to submit assessment plan. Please try again.",
        "error"
      );
    }
  };

  // Function to check if all mandatory assessors are allocated
  const checkMandatoryAssessorsOld = () => {
    // Check if at least one LA exists
    const hasLA = allocatedAssessors.some(
      (assessor) => assessor.capacity === "LA" || assessor.capacity === "LA_TA"
    );
    if (!hasLA) {
      return false;
    }
    // Check if each discipline has at least one TA
    for (const discipline of disciplineList) {
      const hasTAForDiscipline = allocatedAssessors.some((assessor) => {
        const isValidCapacity =
          assessor.capacity === "TA" ||
          assessor.capacity === "TE" ||
          assessor.capacity === "OBS" ||
          assessor.capacity === "LA_TA";

        const disciplineMatches =
          assessor.Discipline &&
          String(assessor.Discipline).includes(String(discipline.id));

        return isValidCapacity && disciplineMatches;
      });
      if (!hasTAForDiscipline) {
        return false;
      }
    }
    return true;
  };

  // Function to check if all mandatory assessors are allocated
  const checkMandatoryAssessors = () => {
    // Check if at least one LA exists
    const hasLA = allocatedAssessors.some(
      (assessor) => assessor.capacity === "LA" || assessor.capacity === "LA_TA"
    );
    if (!hasLA) {
      return false;
    }
    // Check if each discipline (and group, if required) has at least one TA
    for (const discipline of disciplineList) {
      const hasTAForDiscipline = allocatedAssessors.some((assessor) => {
        const isValidCapacity =
          assessor.capacity === "TA" ||
          assessor.capacity === "TE" ||
          assessor.capacity === "OBS" ||
          assessor.capacity === "LA_TA";

        if (labTypeId === 1 || labTypeId === 2) {
          // Discipline and group both must match
          const disciplineIds = assessor.disciplineIds?.map(String) || [];
          const groupIds = assessor.groupIds?.map(String) || [];
          const disciplineMatch = disciplineIds.includes(String(discipline.id));
          const groupMatch = groupIds.includes(String(discipline.groupId));
          return isValidCapacity && disciplineMatch && groupMatch;
        } else {
          // Only discipline match
          const disciplineMatches =
            assessor.Discipline &&
            String(assessor.Discipline).includes(String(discipline.id));
          return isValidCapacity && disciplineMatches;
        }
      });
      if (!hasTAForDiscipline) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    checkMandatoryAssessors();
  }, [allocatedAssessors]);

  // Function to check if rollback button should be shown
  // Show rollback button only if startdate is equal to or greater than today
  const shouldShowRollbackButton = () => {
    if (!startDate) {
      console.log('🔍 Rollback button: No start date available');
      return false; // If no start date, don't show
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for date-only comparison
    
    const assessmentStartDate = new Date(startDate);
    assessmentStartDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const shouldShow = assessmentStartDate >= today;
    
    console.log('🔍 Rollback button visibility check:', {
      startDate,
      today: today.toISOString().split('T')[0],
      assessmentStartDate: assessmentStartDate.toISOString().split('T')[0],
      shouldShow
    });
    
    // Show rollback button if start date is today or in the future
    return shouldShow;
  };

  // Function to handle Send to CAB - Open password dialog
  const handleSendToCAB = () => {
    setPasswordDialog({ open: true, password: "", loading: false, showPassword: false });
  };

  // Function to confirm Send to CAB with password
  const confirmSendToCAB = async () => {
    if (!passwordDialog.password || passwordDialog.password.trim() === "") {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setPasswordDialog(prev => ({ ...prev, loading: true }));

    try {
      // Verify password using the decrypted orgId from URL
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: localStorage.getItem("orgId") || "",
          password: passwordDialog.password
        }
      );

      if (!response.data.success) {
        showSnackbar("Invalid password. Please try again.", "error");
        setPasswordDialog(prev => ({ ...prev, loading: false }));
        return;
      }

      // Password verified, proceed with sending to CAB
      await StageService.updateApplicationStage(orgId, appId, "19e");
      showSnackbar("Sent to CAB successfully", "success");
      setPasswordDialog({ open: false, password: "", loading: false, showPassword: false });

      setTimeout(() => {
        navigate("/main/do/dashboard");
      }, 2000);
    } catch (err) {
      console.error("Error sending to CAB:", err);
      showSnackbar(err.response?.data?.message || "Unable to send to CAB. Please try again.", "error");
      setPasswordDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Snackbar helper function
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
      timeout: 2000,
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: "100vh",
        py: 1,
      }}
    >
      <Box sx={{ px: { xs: 2, md: 2 }, maxWidth: "1400px", mx: "auto" }}>
        {/* Lab Information Card */}
        <Card
          sx={{
            mb: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1.5,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {labDetails.labName || "-"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {labDetails.Address || "-"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={`Lab-ID: ${labDetails.Labid || "-"}`}
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: 1,
                        height: 32,
                        "& .MuiChip-label": { px: 2 },
                      }}
                    />
                    <Chip
                      label={`Lab Type: ${
                        getLabTypeString(labDetails.LabType) || "-"
                      }`}
                      sx={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: 1,
                        height: 32,
                        "& .MuiChip-label": { px: 2 },
                      }}
                    />
                    <Chip
                      label={`Standard: ${labDetails.Standard || "-"}`}
                      sx={{
                        background:
                          "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: 1,
                        height: 32,
                        "& .MuiChip-label": { px: 2 },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", minWidth: 120 }}
                    >
                      Current Stage:
                    </Typography>
                    <Chip
                      label={cabStage || "-"}
                      color="warning"
                      variant="filled"
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="contained"
                    startIcon={<ContactIcon />}
                    onClick={() => setOpenCabDialog(true)}
                    sx={{
                      textTransform: "none",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                        boxShadow: "0 6px 16px rgba(102,126,234,0.6)",
                      },
                    }}
                  >
                    CAB Contact Details
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Rest of your existing component code remains the same */}
        {/* Main Content (full width now) */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            {/* Combined Tables Section - Enhanced Design */}
            <Card
              sx={{
                mb: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  p: 2.5,
                  color: "white",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, letterSpacing: "-0.5px" }}
                >
                  📋 Assessment Planning Details
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ overflow: "auto" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "primary.main",
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          Dates Chosen by CAB
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "primary.main",
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          From Date
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "primary.main",
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          To Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datesData.map((date, index) => (
                        <TableRow
                          key={date.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#fafafa",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: "medium",
                              textAlign: "center",
                            }}
                          >
                            {date.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              color: "success.main",
                              fontWeight: "medium",
                            }}
                          >
                            {date.from}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              color: "success.main",
                              fontWeight: "medium",
                            }}
                          >
                            {date.to}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Integrated Assessment and Additional Questions */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid style={{ display: "none" }} item xs={12} md={6}>
                    <FormControl component="fieldset">
                      <FormLabel
                        component="legend"
                        sx={{ fontWeight: "bold", mb: 0 }}
                      >
                        Is this integrated assessment
                      </FormLabel>
                      <RadioGroup
                        row
                        value={isIntegratedAssessment}
                        onChange={handleIntegratedAssessmentChange}
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="No"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Date Selection Table */}
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                width: "30%",
                              }}
                            >
                              Assessment Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                width: "35%",
                              }}
                            >
                              Start Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                width: "35%",
                              }}
                            >
                              End Date
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Primary Assessment Date Row */}
                          <TableRow>
                            <TableCell sx={{ fontWeight: "medium" }}>
                              Slot 1
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                inputProps={{
                                  min: getMinDate(),
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                InputProps={{ readOnly: true }}
                              />
                            </TableCell>
                          </TableRow>

                          {/* Additional Slot Row - Only for Non-Integrated */}
                          {isIntegratedAssessment === "No" && (
                            <>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "medium" }}>
                                  <FormControl component="fieldset">
                                    <FormLabel
                                      component="legend"
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      Slot 2
                                    </FormLabel>
                                    <RadioGroup
                                      row
                                      value={oneMoreSlot}
                                      onChange={(e) =>
                                        setOneMoreSlot(e.target.value)
                                      }
                                    >
                                      <FormControlLabel
                                        value="Yes"
                                        control={<Radio size="small" />}
                                        label="Yes"
                                      />
                                      <FormControlLabel
                                        value="No"
                                        control={<Radio size="small" />}
                                        label="No"
                                      />
                                    </RadioGroup>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  {oneMoreSlot === "Yes" && (
                                    <TextField
                                      fullWidth
                                      type="date"
                                      value={additionalStartDate}
                                      onChange={(e) => {
                                        const selectedDate = e.target.value;
                                        if (
                                          endDate &&
                                          new Date(selectedDate) <=
                                            new Date(endDate)
                                        ) {
                                          showSnackbar(
                                            "Additional start date must be after the end date",
                                            "error",
                                          );
                                          return;
                                        }
                                        setAdditionalStartDate(selectedDate);
                                        if (selectedDate) {
                                          setAdditionalEndDate(
                                            getNextDay(selectedDate),
                                          );
                                        }
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      size="small"
                                      inputProps={{
                                        min: endDate,
                                      }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {oneMoreSlot === "Yes" && (
                                    <TextField
                                      fullWidth
                                      type="date"
                                      value={additionalEndDate}
                                      InputLabelProps={{ shrink: true }}
                                      size="small"
                                      InputProps={{ readOnly: true }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>

                              {/* Reason Row */}
                              {oneMoreSlot === "Yes" && (
                                <TableRow>
                                  <TableCell sx={{ fontWeight: "medium" }}>
                                    Reason for Slot 2
                                  </TableCell>
                                  <TableCell colSpan={2}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={2}
                                      value={reason}
                                      onChange={(e) =>
                                        setReason(e.target.value)
                                      }
                                      size="small"
                                      placeholder="Enter reason for slot 2"
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  {(labDetails.Stage === "19a" ||
                    labDetails.Stage === "19d" ||
                    labDetails.Stage === "19f" ||
                    labDetails.Stage === "19h") && (
                    <Button
                      variant="contained"
                      onClick={handleSubmitAssessmentPlan}
                      size="large"
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        px: 5,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                          boxShadow: "0 6px 16px rgba(102,126,234,0.6)",
                        },
                      }}
                    >
                      {hasIntegratedData
                        ? "Update Assessment Slot"
                        : "Submit Assessment Slot"}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Disciplines/Groups Card */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Card
                sx={{
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  borderRadius: 3,
                  mt: 3,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    p: 2.5,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    👥 Proposed Assessment Team
                  </Typography>
                  {shouldShowRollbackButton() && (
                    <Button
                      variant="contained"
                      onClick={handleRollbackAssessment}
                      size="small"
                      sx={{
                        mt: 1,
                      }}
                    >
                      Rollback Entire Assessment
                    </Button>
                  )}
                </Box>
                <CardContent sx={{ p: 3 }}>
                  {/* Lead Assessors Table - LA & LA_TA (Without Discipline Grouping) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      🎯 Lead Assessors
                    </Typography>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Assessor Name
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Capacity
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Start Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            End Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Add Assessor Row */}
                        <TableRow
                          sx={{
                            backgroundColor: "rgba(102,126,234,0.05)",
                            "&:hover": {
                              backgroundColor: "rgba(102,126,234,0.1)",
                            },
                          }}
                        >
                          <TableCell colSpan={4}></TableCell>
                          <TableCell>
                            {(labDetails.Stage === "19a" ||
                              labDetails.Stage === "19d" ||
                              labDetails.Stage === "19f" ||
                              labDetails.Stage === "19h") && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenAssessorDialog(null, null, "LA")
                                }
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  color: "white",
                                  boxShadow: "0 2px 8px rgba(102,126,234,0.3)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                                    boxShadow:
                                      "0 4px 12px rgba(102,126,234,0.5)",
                                  },
                                }}
                              >
                                Add Assessor
                              </Button>
                            )}
                            {labDetails.Stage === "20" && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenAssessorDialog(null, null, "LA")
                                }
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background:
                                    "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                                  color: "white",
                                  boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)",
                                    boxShadow: "0 4px 12px rgba(76,175,80,0.5)",
                                  },
                                }}
                              >
                                Add New Assessor
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Display LA and LA_TA assessors without discipline grouping */}
                        {/* Show LA_TA in both cases: null discipline OR with discipline value */}
                        {allocatedAssessors
                          .filter((assessor) => {
                            // Show LA always (they don't have discipline)
                            if (assessor.capacity === "LA") return true;

                            // Show LA_TA always (both with and without discipline)
                            if (assessor.capacity === "LA_TA") {
                              return true;
                            }

                            return false;
                          })
                          // Remove duplicates - show each LA_TA only once in Lead Assessor table
                          .filter((assessor, index, arr) => {
                            // For LA, always include (no duplicates expected)
                            if (assessor.capacity === "LA") return true;

                            // For LA_TA, only include the first occurrence of each assessor
                            if (assessor.capacity === "LA_TA") {
                              return (
                                arr.findIndex(
                                  (a) =>
                                    a.assessorId === assessor.assessorId &&
                                    a.capacity === "LA_TA",
                                ) === index
                              );
                            }

                            return false;
                          })
                          .map((assessor) => (
                            <TableRow key={assessor.id}>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Typography variant="body2">
                                    {assessor.assessor}
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      handleViewProfile(assessor.assessorId)
                                    }
                                    sx={{
                                      borderColor: "primary.main",
                                      color: "primary.main",
                                    }}
                                  >
                                    View Profile
                                  </Button>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={assessor.capacity}
                                  size="small"
                                  sx={{
                                    background:
                                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  assessor.startDate,
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  assessor.endDate,
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {(labDetails.Stage === "19a" ||
                                  labDetails.Stage === "19d" ||
                                  labDetails.Stage === "19f" ||
                                  labDetails.Stage === "19h") && (
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    {/* <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleEditAssessor(assessor)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton> */}
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        handleDeleteAllocation(
                                          assessor.id,
                                          assessor.Discipline,
                                          assessor.Group,
                                        )
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                                {labDetails.Stage === "20" && (
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <IconButton
                                      color="warning"
                                      size="small"
                                      onClick={() =>
                                        handleRejectAssessor(assessor)
                                      }
                                      title="Reject Assessor"
                                    >
                                      <RejectIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}

                        {allocatedAssessors.filter((assessor) => {
                          // Show LA always
                          if (assessor.capacity === "LA") return true;

                          // Show LA_TA always (both with and without discipline)
                          if (assessor.capacity === "LA_TA") {
                            return true;
                          }

                          return false;
                        }).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontStyle: "italic",
                                  textAlign: "center",
                                }}
                              >
                                No lead assessors allocated
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Observers Table - OBS (no discipline required) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "2px solid",
                      borderColor: "success.main",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "success.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      👁 Observers
                    </Typography>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}>
                            Assessor Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}>
                            Capacity
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}>
                            Start Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}>
                            End Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "0.95rem" }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow
                          sx={{
                            backgroundColor: "rgba(17,153,142,0.05)",
                            "&:hover": { backgroundColor: "rgba(17,153,142,0.1)" },
                          }}
                        >
                          <TableCell colSpan={4}></TableCell>
                          <TableCell>
                            {(labDetails.Stage === "19a" ||
                              labDetails.Stage === "19d" ||
                              labDetails.Stage === "19f" ||
                              labDetails.Stage === "19h") && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenAssessorDialog(null, null, "OBS")}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                                  color: "white",
                                  boxShadow: "0 2px 8px rgba(17,153,142,0.3)",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #0e8073 0%, #2ed66c 100%)",
                                    boxShadow: "0 4px 12px rgba(17,153,142,0.5)",
                                  },
                                }}
                              >
                                Add Observer
                              </Button>
                            )}
                            {labDetails.Stage === "20" && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenAssessorDialog(null, null, "OBS")}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                                  color: "white",
                                  boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)",
                                    boxShadow: "0 4px 12px rgba(76,175,80,0.5)",
                                  },
                                }}
                              >
                                Add New Observer
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>

                        {allocatedAssessors
                          .filter((assessor) => assessor.capacity === "OBS")
                          .map((assessor) => (
                            <TableRow key={assessor.id}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <Typography variant="body2">{assessor.assessor}</Typography>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleViewProfile(assessor.assessorId)}
                                    sx={{ borderColor: "success.main", color: "success.main" }}
                                  >
                                    View Profile
                                  </Button>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label="OBS"
                                  size="small"
                                  sx={{
                                    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(assessor.startDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {new Date(assessor.endDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {(labDetails.Stage === "19a" ||
                                  labDetails.Stage === "19d" ||
                                  labDetails.Stage === "19f" ||
                                  labDetails.Stage === "19h") && (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      handleDeleteAllocation(
                                        assessor.id,
                                        assessor.Discipline,
                                        assessor.Group,
                                      )
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {labDetails.Stage === "20" && (
                                  <IconButton
                                    color="warning"
                                    size="small"
                                    onClick={() => handleRejectAssessor(assessor)}
                                    title="Reject Assessor"
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}

                        {allocatedAssessors.filter((a) => a.capacity === "OBS").length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic", textAlign: "center" }}
                              >
                                No observers allocated
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Other Assessors Table - TA, TE */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "2px solid",
                      borderColor: "secondary.main",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "secondary.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      👥 Other Team
                    </Typography>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            {labTypeId === 1 || labTypeId === 2
                              ? "Discipline/Group Name"
                              : "Discipline Name"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Assessor Name
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Capacity
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Start Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            End Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              fontSize: "0.95rem",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {disciplineList.map((discipline) => {
                          const disciplineAssessors = allocatedAssessors.filter(
                            (assessor) => {
                              const isOther =
                                assessor.capacity === "TA" ||
                                assessor.capacity === "TE";
                              const isLA_TA = assessor.capacity === "LA_TA";
                              const matchesCapacity = isOther || isLA_TA;

                              if (labTypeId === 1 || labTypeId === 2) {
                                // Match both discipline and group
                                const disciplineIds =
                                  assessor.disciplineIds?.map(String) || [];
                                const groupIds =
                                  assessor.groupIds?.map(String) || [];
                                return (
                                  disciplineIds.includes(
                                    String(discipline.id),
                                  ) &&
                                  groupIds.includes(
                                    String(discipline.groupId),
                                  ) &&
                                  matchesCapacity
                                );
                              } else {
                                // Old logic for other lab types
                                if (
                                  assessor.disciplines &&
                                  Array.isArray(assessor.disciplines)
                                ) {
                                  return (
                                    assessor.disciplines.includes(
                                      discipline.name,
                                    ) && matchesCapacity
                                  );
                                }
                                return (
                                  assessor.discipline === discipline.name &&
                                  matchesCapacity
                                );
                              }
                            },
                          );
                          // const disciplineAssessors = allocatedAssessors.filter(
                          //   (assessor) => {
                          //     // Filter for TA, TE, OBS
                          //     const isOther =
                          //       assessor.capacity === "TA" ||
                          //       assessor.capacity === "TE" ||
                          //       assessor.capacity === "OBS";

                          //     // Include ALL LA_TA assessors (both with and without discipline)
                          //     const isLA_TA = assessor.capacity === "LA_TA";

                          //     const matchesCapacity = isOther || isLA_TA;

                          //     // support new 'disciplines' array or legacy 'discipline' string
                          //     if (
                          //       assessor.disciplines &&
                          //       Array.isArray(assessor.disciplines)
                          //     ) {
                          //       return (
                          //         assessor.disciplines.includes(
                          //           discipline.name
                          //         ) && matchesCapacity
                          //       );
                          //     }
                          //     return (
                          //       assessor.discipline === discipline.name &&
                          //       matchesCapacity
                          //     );
                          //   }
                          // );

                          return (
                            <React.Fragment key={`other-${discipline.id}`}>
                              <TableRow
                                sx={{
                                  background:
                                    "linear-gradient(90deg, rgba(240,147,251,0.15) 0%, rgba(245,87,108,0.15) 100%)",
                                  borderLeft: "4px solid",
                                  borderLeftColor: "secondary.main",
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    borderBottom: "none",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {labTypeId === 1 || labTypeId === 2 ? (
                                        <>
                                          <span style={{ color: "#1976d2" }}>
                                            {discipline.name}
                                          </span>
                                          {" / "}
                                          <span style={{ color: "#d32f2f" }}>
                                            {discipline.groupName}
                                          </span>
                                        </>
                                      ) : (
                                        <span style={{ color: "#1976d2" }}>
                                          {discipline.name}
                                        </span>
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.82rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Total Scope: {discipline.totalscope}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.82rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Qualitative: {discipline.Qualitative}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.82rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Quantitative: {discipline.Quantitative}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.82rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Calculated: {discipline.Calculated}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  colSpan={4}
                                  sx={{ borderBottom: "none" }}
                                ></TableCell>
                                <TableCell sx={{ borderBottom: "none" }}>
                                  {(labDetails.Stage === "19a" ||
                                    labDetails.Stage === "19d" ||
                                    labDetails.Stage === "19f" ||
                                    labDetails.Stage === "19h") && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() =>
                                        handleOpenAssessorDialog(
                                          discipline.id,
                                          discipline.groupId,
                                          "OTHER",
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        background:
                                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                        color: "white",
                                        boxShadow:
                                          "0 2px 8px rgba(240,147,251,0.3)",
                                        "&:hover": {
                                          background:
                                            "linear-gradient(135deg, #e082ea 0%, #e4465b 100%)",
                                          boxShadow:
                                            "0 4px 12px rgba(240,147,251,0.5)",
                                        },
                                      }}
                                    >
                                      Add Assessor
                                    </Button>
                                  )}
                                  {labDetails.Stage === "20" && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() =>
                                        handleOpenAssessorDialog(
                                          discipline.id,
                                          discipline.groupId,
                                          "OTHER",
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        background:
                                          "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                                        color: "white",
                                        boxShadow:
                                          "0 2px 8px rgba(76,175,80,0.3)",
                                        "&:hover": {
                                          background:
                                            "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)",
                                          boxShadow:
                                            "0 4px 12px rgba(76,175,80,0.5)",
                                        },
                                      }}
                                    >
                                      Add New Assessor
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                              {disciplineAssessors.map((assessor) => (
                                <TableRow key={assessor.id}>
                                  <TableCell></TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {assessor.assessor}
                                      </Typography>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                          handleViewProfile(assessor.assessorId)
                                        }
                                        sx={{
                                          borderColor: "primary.main",
                                          color: "primary.main",
                                        }}
                                      >
                                        View Profile
                                      </Button>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={assessor.capacity}
                                      size="small"
                                      sx={{
                                        background:
                                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                        color: "white",
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {new Date(
                                      assessor.startDate,
                                    ).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(
                                      assessor.endDate,
                                    ).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    {(labDetails.Stage === "19a" ||
                                      labDetails.Stage === "19d" ||
                                      labDetails.Stage === "19f" ||
                                      labDetails.Stage === "19h") && (
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        {/* <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                              handleEditAssessor(assessor)
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton> */}
                                        <IconButton
                                          color="error"
                                          size="small"
                                          onClick={() =>
                                            handleDeleteAllocation(
                                              assessor.id,
                                              assessor.Discipline,
                                              assessor.Group,
                                              discipline.id,
                                              discipline.groupId,
                                            )
                                          }
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    )}
                                    {labDetails.Stage === "20" && (
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <IconButton
                                          color="warning"
                                          size="small"
                                          onClick={() =>
                                            handleRejectAssessor(assessor)
                                          }
                                          title="Reject Assessor"
                                        >
                                          <RejectIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {disciplineAssessors.length === 0 && (
                                <TableRow>
                                  <TableCell></TableCell>
                                  <TableCell colSpan={5}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      No assessors allocated
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Rejected Assessors Section */}
                  {rejectedAssessors.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          mt: 4,
                          pb: 1,
                          borderBottom: "2px solid",
                          borderColor: "error.main",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "error.main",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          ❌ Rejected Assessors
                        </Typography>
                      </Box>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                background:
                                  "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Discipline
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Assessor Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Capacity
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Date Range
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Rejected By
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Rejected Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Remarks
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rejectedAssessors.map((assessor, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "rgba(211, 47, 47, 0.05)",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {assessor.discipline || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {assessor.assessor}
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() =>
                                        handleViewProfile(assessor.assessorId)
                                      }
                                      sx={{
                                        borderColor: "error.main",
                                        color: "error.main",
                                        "&:hover": {
                                          borderColor: "error.dark",
                                          backgroundColor:
                                            "rgba(211, 47, 47, 0.08)",
                                        },
                                      }}
                                    >
                                      View Profile
                                    </Button>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={assessor.capacity}
                                    size="small"
                                    sx={{
                                      background:
                                        "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block" }}
                                  >
                                    {new Date(
                                      assessor.startDate,
                                    ).toLocaleDateString()}{" "}
                                    -{" "}
                                    {new Date(
                                      assessor.endDate,
                                    ).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={assessor.rejectedBy || "Unknown"}
                                    size="small"
                                    sx={{
                                      background:
                                        assessor.rejectedBy === "CAB"
                                          ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                          : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block" }}
                                  >
                                    {assessor.CabAcceptDate
                                      ? new Date(
                                          assessor.CabAcceptDate,
                                        ).toLocaleDateString()
                                      : assessor.AssessorAcceptDate
                                        ? new Date(
                                            assessor.AssessorAcceptDate,
                                          ).toLocaleDateString()
                                        : assessor.DoRejectDate
                                          ? new Date(
                                              assessor.DoRejectDate,
                                            ).toLocaleDateString()
                                          : "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "error.main",
                                      fontStyle: "italic",
                                      display: "block",
                                      maxWidth: 250,
                                    }}
                                  >
                                    {assessor.remarks || "No remarks provided"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Send to CAB Button */}
              {(labDetails.Stage === "19a" ||
                labDetails.Stage === "19d" ||
                labDetails.Stage === "19f" ||
                labDetails.Stage === "19h") &&
                checkMandatoryAssessors() && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 4,
                      mb: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSendToCAB}
                      sx={{
                        minWidth: 250,
                        py: 1.5,
                        background:
                          "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "1rem",
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(17,153,142,0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0e8073 0%, #2ed66c 100%)",
                          boxShadow: "0 6px 16px rgba(17,153,142,0.6)",
                        },
                      }}
                    >
                      ✅ Send to CAB
                    </Button>
                  </Box>
                )}
              {labDetails.Stage === "19a" && !checkMandatoryAssessors() && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 4,
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    border: "1px solid rgba(211, 47, 47, 0.3)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "error.main",
                      fontStyle: "italic",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    ⚠️ Please allocate at least one Lead Assessor (LA/LA_TA) and
                    one Technical Assessor (TA/TE/LA_TA) for each discipline
                    before sending to CAB.
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Password Confirmation Dialog for Send to CAB */}
            <Dialog
              open={passwordDialog.open}
              onClose={() =>
                !passwordDialog.loading &&
                setPasswordDialog({
                  open: false,
                  password: "",
                  loading: false,
                  showPassword: false,
                })
              }
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  background:
                    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  color: "white",
                  p: 2.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  ✅ Confirm Send to CAB
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ p: 3, mt: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Please enter your password to confirm sending this application
                  to CAB.
                </Typography>
                <TextField
                  fullWidth
                  type={passwordDialog.showPassword ? "text" : "password"}
                  label="Enter Your Password"
                  placeholder="Enter password"
                  value={passwordDialog.password}
                  onChange={(e) =>
                    setPasswordDialog((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  disabled={passwordDialog.loading}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !passwordDialog.loading) {
                      confirmSendToCAB();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setPasswordDialog((prev) => ({
                              ...prev,
                              showPassword: !prev.showPassword,
                            }))
                          }
                          edge="end"
                          sx={{ mr: 0.1 }}
                          disabled={passwordDialog.loading}
                        >
                          {passwordDialog.showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </DialogContent>
              <DialogActions sx={{ p: 2.5, gap: 1 }}>
                <Button
                  onClick={() =>
                    setPasswordDialog({
                      open: false,
                      password: "",
                      loading: false,
                      showPassword: false,
                    })
                  }
                  disabled={passwordDialog.loading}
                  sx={{
                    color: "text.secondary",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmSendToCAB}
                  variant="contained"
                  disabled={passwordDialog.loading || !passwordDialog.password}
                  sx={{
                    background:
                      "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    color: "white",
                    fontWeight: 700,
                    textTransform: "none",
                    px: 3,
                    borderRadius: 2,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0e8073 0%, #2ed66c 100%)",
                    },
                    "&:disabled": {
                      background: "rgba(0,0,0,0.12)",
                      color: "rgba(0,0,0,0.26)",
                    },
                  }}
                >
                  {passwordDialog.loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Confirm & Send"
                  )}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Assessor Allocation Dialog */}
            <Dialog
              open={openAssessorDialog}
              onClose={() => {
                setOpenAssessorDialog(false);
                setAssessorSearchFilter("");
                setStateFilter(null);
                setCityFilter(null);
              }}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  p: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <AddIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Add Assessor
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Search Section */}
                  {assessorType === "LA" ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Capacity</InputLabel>
                          <Select
                            value={dialogCapacity}
                            label="Select Capacity"
                            onChange={(e) => {
                              const newCapacity = e.target.value;
                              if (!validateCapacitySelection(newCapacity)) {
                                return;
                              }
                              setDialogCapacity(newCapacity);
                              setLaToSendAs(""); // Reset LA send as when capacity changes
                              setShowAvailableAssessors(false);
                              setDialogSelectedAssessor("");
                            }}
                          >
                            <MenuItem value="">
                              <em>Select Capacity</em>
                            </MenuItem>
                            <MenuItem value="LA">Lead Assessor</MenuItem>
                            <MenuItem value="LA_TA">
                              Lead + Technical Assessor
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Show additional question when LA_TA is selected */}
                      {dialogCapacity === "LA_TA" && (
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Send Assessor As</InputLabel>
                            <Select
                              value={laToSendAs}
                              label="Send Assessor As"
                              onChange={(e) => {
                                setLaToSendAs(e.target.value);
                                setShowAvailableAssessors(false);
                                setDialogSelectedAssessor("");
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Role</em>
                              </MenuItem>
                              <MenuItem value="LA">
                                Send as Lead Assessor Only
                              </MenuItem>
                              <MenuItem value="LATA">
                                Send as Lead + Technical Assessor
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </>
                  ) : assessorType === "OBS" ? (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Capacity</InputLabel>
                        <Select
                          value={dialogCapacity}
                          label="Select Capacity"
                          onChange={(e) => {
                            setDialogCapacity(e.target.value);
                            setShowAvailableAssessors(false);
                            setDialogSelectedAssessor("");
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Capacity</em>
                          </MenuItem>
                          <MenuItem value="OBS">Observer</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Capacity</InputLabel>
                        <Select
                          value={dialogCapacity}
                          label="Select Capacity"
                          onChange={(e) => {
                            const newCapacity = e.target.value;
                            setDialogCapacity(newCapacity);
                            setShowAvailableAssessors(false);
                            setDialogSelectedAssessor("");
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Capacity</em>
                          </MenuItem>
                          <MenuItem value="TA">Technical Assessor</MenuItem>
                          <MenuItem value="TE">Technical Expert</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {(dialogCapacity === "TA" ||
                    dialogCapacity === "TE" ||
                    dialogCapacity === "OBS") && assessorType === "OTHER" && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Discipline</InputLabel>
                          <Select
                            value={
                              dialogDisciplines.length > 0
                                ? dialogDisciplines[0]
                                : ""
                            }
                            label="Discipline"
                            readOnly={true}
                            disabled={true}
                            onChange={(e) => {
                              setDialogDisciplines(
                                e.target.value ? [e.target.value] : [],
                              );
                              setShowAvailableAssessors(false);
                              setDialogSelectedAssessor("");
                            }}
                          >
                            <MenuItem value="">
                              <em>Select Discipline</em>
                            </MenuItem>
                            {disciplineList.map((discipline) => (
                              <MenuItem
                                key={discipline.id}
                                value={discipline.id}
                              >
                                {discipline.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {(labTypeId === 1 || labTypeId === 2) && (
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Group</InputLabel>
                            <Select
                              value={
                                dialogGroups.length > 0 ? dialogGroups[0] : ""
                              }
                              label="Group"
                              readOnly={true}
                              disabled={true}
                              onChange={(e) => {
                                setDialogGroups(
                                  e.target.value ? [e.target.value] : [],
                                );
                                setShowAvailableAssessors(false);
                                setDialogSelectedAssessor("");
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Group</em>
                              </MenuItem>
                              {disciplineList.map((group) => (
                                <MenuItem
                                  key={group.groupId}
                                  value={group.groupId}
                                >
                                  {group.groupName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Date From"
                      type="date"
                      value={dialogStartDate}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        setDialogStartDate(newStartDate);
                        if (
                          dialogEndDate &&
                          new Date(dialogEndDate) < new Date(newStartDate)
                        ) {
                          setDialogEndDate("");
                        }
                        setShowAvailableAssessors(false);
                        setDialogSelectedAssessor("");
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      inputProps={{
                        min:
                          startDate || new Date().toISOString().split("T")[0],
                        max:
                          oneMoreSlot === "Yes" && additionalEndDate
                            ? additionalEndDate
                            : endDate,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Date To"
                      type="date"
                      value={dialogEndDate}
                      onChange={(e) => {
                        const newEndDate = e.target.value;
                        if (
                          dialogStartDate &&
                          new Date(newEndDate) < new Date(dialogStartDate)
                        ) {
                          showSnackbar(
                            "End date cannot be less than start date",
                            "error",
                          );
                          return;
                        }
                        setDialogEndDate(newEndDate);
                        setShowAvailableAssessors(false);
                        setDialogSelectedAssessor("");
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      inputProps={{
                        min: dialogStartDate || startDate,
                        max:
                          oneMoreSlot === "Yes" && additionalEndDate
                            ? additionalEndDate
                            : endDate,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleCheckAvailableAssessors}
                      disabled={
                        !dialogCapacity ||
                        !dialogStartDate ||
                        !dialogEndDate ||
                        searchingAssessors
                      }
                      sx={{
                        width: { xs: "100%", md: "auto" },
                        minWidth: 220,
                        py: 1.2,
                        background:
                          "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        color: "white",
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(79,172,254,0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #3e9bec 0%, #00d9ec 100%)",
                          boxShadow: "0 6px 16px rgba(79,172,254,0.6)",
                        },
                        "&.Mui-disabled": {
                          background:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                          color: "white",
                          opacity: 0.7,
                        },
                      }}
                      startIcon={
                        searchingAssessors ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      {searchingAssessors
                        ? "Searching..."
                        : "Search Available Assessors"}
                    </Button>
                  </Grid>

                  {/* Available Assessors Table */}
                  {showAvailableAssessors && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          mt: 2,
                          pb: 1,
                          borderBottom: "2px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            fontSize: "1.1rem",
                          }}
                        >
                          🔍 Available Assessors
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Search Assessor"
                              placeholder="Search by name..."
                              value={assessorSearchFilter}
                              onChange={(e) =>
                                setAssessorSearchFilter(e.target.value)
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon
                                      sx={{ color: "primary.main" }}
                                    />
                                  </InputAdornment>
                                ),
                                endAdornment: assessorSearchFilter && (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setAssessorSearchFilter("")
                                      }
                                      edge="end"
                                      sx={{
                                        color: "text.secondary",
                                        "&:hover": {
                                          color: "error.main",
                                          backgroundColor:
                                            "rgba(211, 47, 47, 0.08)",
                                        },
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  "&:hover fieldset": {
                                    borderColor: "primary.main",
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              size="small"
                              options={states}
                              getOptionLabel={(option) => option.name || option}
                              value={stateFilter}
                              onChange={(e, newValue) => {
                                setStateFilter(newValue);
                                setCityFilter(null);
                                if (newValue?.id) {
                                  loadCities(newValue.id);
                                } else {
                                  setCities([]);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Filter by State"
                                  placeholder="Select state"
                                />
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              size="small"
                              options={cities}
                              getOptionLabel={(option) => option.name || option}
                              value={cityFilter}
                              onChange={(e, newValue) =>
                                setCityFilter(newValue)
                              }
                              disabled={!stateFilter}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Filter by City"
                                  placeholder="Select city"
                                />
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          maxHeight: 400,
                          overflowY: "auto",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background:
                              "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                            borderRadius: "4px",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #3e9bec 0%, #00d9ec 100%)",
                            },
                          },
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow
                              sx={{
                                background:
                                  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                  background:
                                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                }}
                              >
                                Assessor Name (Disciplines)
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.95rem",
                                  background:
                                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                }}
                              >
                                View Profile
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(() => {
                              const filteredAssessors =
                                availableAssessorsList.filter((assessor) => {
                                  // Filter by name search
                                  if (assessorSearchFilter) {
                                    const searchLower =
                                      assessorSearchFilter.toLowerCase();
                                    if (
                                      !assessor.name
                                        .toLowerCase()
                                        .includes(searchLower)
                                    ) {
                                      return false;
                                    }
                                  }
                                  // Filter by state (contains match)
                                  if (stateFilter) {
                                    const stateName =
                                      stateFilter.name || stateFilter;
                                    if (
                                      !assessor.address
                                        ?.toLowerCase()
                                        .includes(stateName.toLowerCase())
                                    ) {
                                      return false;
                                    }
                                  }
                                  // Filter by city (contains match)
                                  if (cityFilter) {
                                    const cityName =
                                      cityFilter.name || cityFilter;
                                    if (
                                      !assessor.address
                                        ?.toLowerCase()
                                        .includes(cityName.toLowerCase())
                                    ) {
                                      return false;
                                    }
                                  }
                                  return true;
                                });

                              if (filteredAssessors.length === 0) {
                                return (
                                  <TableRow>
                                    <TableCell colSpan={2}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          py: 2,
                                          gap: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            color: "text.secondary",
                                            fontWeight: 500,
                                          }}
                                        >
                                          😕 No Assessors Found
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "text.secondary",
                                            fontStyle: "italic",
                                            textAlign: "center",
                                          }}
                                        >
                                          {assessorSearchFilter ||
                                          stateFilter ||
                                          cityFilter
                                            ? "No assessors match your search criteria. Try adjusting your filters."
                                            : "No assessors available for the selected criteria."}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              }

                              return filteredAssessors.map((assessor) => (
                                <TableRow
                                  key={assessor.id}
                                  sx={{
                                    backgroundColor:
                                      dialogSelectedAssessor === assessor.id
                                        ? "action.selected"
                                        : "inherit",
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    assessor.available &&
                                    setDialogSelectedAssessor(assessor.id)
                                  }
                                >
                                  <TableCell width="80%">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Radio
                                        checked={
                                          dialogSelectedAssessor === assessor.id
                                        }
                                        onChange={() =>
                                          setDialogSelectedAssessor(assessor.id)
                                        }
                                        disabled={!assessor.available}
                                        size="small"
                                      />
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 500 }}
                                        >
                                          {assessor.name}
                                        </Typography>
                                        {(assessor.Capacity ||
                                          assessor.disciplines) && (
                                          <>
                                            <Typography
                                              variant="caption"
                                              sx={{ color: "text.secondary" }}
                                            >
                                              {assessor.Capacity &&
                                                `${assessor.Capacity}`}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "blue",
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                              }}
                                            >
                                              Matching Discipline/s:{" "}
                                              {assessor.disciplines}
                                            </Typography>
                                            {(labTypeId === 1 ||
                                              labTypeId === 2) && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "green",
                                                  fontSize: "0.75rem",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Matching Group/s:{" "}
                                                {assessor.matchingGroup}
                                              </Typography>
                                            )}
                                          </>
                                        )}
                                        {assessor.address && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#333",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {" Address- " + assessor.address}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewProfile(assessor.id);
                                      }}
                                    >
                                      View Profile
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ));
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Assignment Section */}
                      <Box
                        sx={{
                          mt: 4,
                          p: 3,
                          borderRadius: 2,
                          background:
                            "linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)",
                          border: "2px solid",
                          borderColor: "primary.light",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2.5,
                            pb: 1,
                            borderBottom: "2px solid",
                            borderColor: "primary.main",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "primary.main",
                              fontSize: "1.1rem",
                            }}
                          >
                            📌 Assign Assessor
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Assessor</InputLabel>
                              <Select
                                value={dialogSelectedAssessor}
                                label="Select Assessor"
                                onChange={(e) => {
                                  setDialogSelectedAssessor(e.target.value);
                                  setDialogDisciplines([]);
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Assessor</em>
                                </MenuItem>
                                {availableAssessorsList
                                  .filter((assessor) => assessor.available)
                                  .map((assessor) => (
                                    <MenuItem
                                      key={assessor.id}
                                      value={assessor.id}
                                    >
                                      {assessor.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {(dialogCapacity === "LA_TA" ||
                            dialogCapacity === "TA" ||
                            dialogCapacity === "TE" ||
                            (dialogCapacity === "OBS" && assessorType === "OTHER")) && labTypeId === 3 && (
                            <>
                              <Grid item xs={12} md={6}>
                                <Autocomplete
                                  multiple
                                  size="small"
                                  options={(() => {
                                    const selectedAssessor =
                                      availableAssessorsList.find(
                                        (a) => a.id === dialogSelectedAssessor,
                                      );
                                    if (
                                      !selectedAssessor ||
                                      !selectedAssessor.disciplineIds
                                    ) {
                                      return disciplineList;
                                    }
                                    const assessorDisciplineIds =
                                      selectedAssessor.disciplineIds
                                        .split(",")
                                        .map((id) => id.trim());
                                    return disciplineList.filter((d) =>
                                      assessorDisciplineIds.includes(
                                        d.id.toString(),
                                      ),
                                    );
                                  })()}
                                  getOptionLabel={(option) => option.name}
                                  value={disciplineList.filter((d) =>
                                    dialogDisciplines.includes(d.id),
                                  )}
                                  onChange={(event, newValue) => {
                                    setDialogDisciplines(
                                      newValue ? newValue.map((v) => v.id) : [],
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Discipline (Matching Only)"
                                      placeholder="Select disciplines"
                                      fullWidth
                                    />
                                  )}
                                  disabled={!dialogSelectedAssessor}
                                />
                              </Grid>
                            </>
                          )}
                        {(dialogCapacity === "LA_TA" ||
                          dialogCapacity === "TA" ||
                          dialogCapacity === "TE" ||
                          (dialogCapacity === "OBS" && assessorType === "OTHER")) &&
                          (labTypeId === 1 || labTypeId === 2) && (() => {

                            // UNIQUE DISCIPLINE OPTIONS (filtered by selected assessor)
                            const selectedAssessor = availableAssessorsList.find(a => a.id === dialogSelectedAssessor);
                            let allowedDisciplineIds = [];
                            if (selectedAssessor && selectedAssessor.disciplineIds) {
                              allowedDisciplineIds = selectedAssessor.disciplineIds.split(',').map(id => Number(id.trim()));
                            }
                            const disciplineOptions = [
                              ...new Map(
                                disciplineList
                                  .filter(item => allowedDisciplineIds.length === 0 || allowedDisciplineIds.includes(Number(item.id)))
                                  .map(item => [Number(item.id), { id: Number(item.id), name: item.name }])
                              ).values(),
                            ];

                            // GROUP OPTIONS: Only groups for selected disciplines
                            const groupOptions = disciplineList
                              .filter((g) => dialogDisciplines.includes(Number(g.id)))
                              .map((g) => ({
                                id: Number(g.id),
                                GroupId: Number(g.groupId || g.GroupId),
                                name: g.name,
                                groupName: g.groupName,
                              }))
                              // Remove duplicates
                              .filter((item, index, self) =>
                                index === self.findIndex((t) =>
                                  t.id === item.id && t.GroupId === item.GroupId
                                )
                              );

                            // When disciplines change, auto-select all groups for those disciplines
                            const handleDisciplineChange = (event, newValue) => {
                              const selectedIds = newValue.map((v) => Number(v.id));
                              setDialogDisciplines(selectedIds);
                              // Auto-select all groups for selected disciplines
                              const newGroups = disciplineList
                                .filter((g) => selectedIds.includes(Number(g.id)))
                                .map((g) => ({
                                  id: Number(g.id),
                                  GroupId: Number(g.groupId || g.GroupId),
                                  name: g.name,
                                  groupName: g.groupName,
                                }))
                                .filter((item, index, self) =>
                                  index === self.findIndex((t) =>
                                    t.id === item.id && t.GroupId === item.GroupId
                                  )
                                );
                              setDialogGroups(newGroups);
                            };

                            // When groups change, just update dialogGroups
                            const handleGroupChange = (event, newValue) => {
                              setDialogGroups(newValue);
                            };

                            return (
                              <>
                                {/* DISCIPLINE */}
                                <Grid item xs={12} md={6}>
                                  <Autocomplete
                                    multiple
                                    size="small"
                                    options={disciplineOptions}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                                    value={disciplineOptions.filter((d) => dialogDisciplines.includes(Number(d.id)))}
                                    onChange={handleDisciplineChange}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Select Discipline"
                                        placeholder="Select disciplines"
                                        fullWidth
                                      />
                                    )}
                                    disabled={!dialogSelectedAssessor}
                                  />
                                </Grid>
                                {/* GROUP */}
                                <Grid item xs={12} md={6}>
                                  <Autocomplete
                                    multiple
                                    size="small"
                                    options={groupOptions}
                                    getOptionLabel={(option) => `${option.name} - ${option.groupName}`}
                                    isOptionEqualToValue={(option, value) =>
                                      Number(option.id) === Number(value.id) && Number(option.GroupId) === Number(value.GroupId)
                                    }
                                    value={groupOptions.filter((option) =>
                                      dialogGroups.some((selected) =>
                                        Number(selected.id) === Number(option.id) && Number(selected.GroupId) === Number(option.GroupId)
                                      )
                                    )}
                                    onChange={handleGroupChange}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Select Group"
                                        placeholder="Select groups"
                                        fullWidth
                                      />
                                    )}
                                    disabled={dialogDisciplines.length === 0}
                                  />
                                </Grid>
                              </>
                            );
                          })()}
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Capacity</InputLabel>
                              <Select
                                value={dialogCapacity}
                                label="Capacity"
                                readOnly={true}
                                disabled={true}
                                onChange={(e) => {
                                  const newCapacity = e.target.value;
                                  setDialogCapacity(newCapacity);
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Capacity</em>
                                </MenuItem>
                                <MenuItem value="LA">Lead Assessor</MenuItem>
                                <MenuItem value="LA_TA">
                                  Lead + Technical Assessor
                                </MenuItem>
                                <MenuItem value="OBS">Observer</MenuItem>
                                <MenuItem value="TA">
                                  Technical Assessor
                                </MenuItem>
                                <MenuItem value="TE">Technical Expert</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions
                sx={{ p: 2.5, gap: 1.5, background: "rgba(0,0,0,0.02)" }}
              >
                <Button
                  onClick={handleCloseAssessorDialog}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAssessor}
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={
                    !dialogCapacity ||
                    !dialogStartDate ||
                    !dialogEndDate ||
                    !dialogSelectedAssessor ||
                    savingAssessor ||
                    (dialogCapacity === "LA_TA" && !laToSendAs) ||
                    (dialogCapacity === "LA_TA" &&
                      laToSendAs === "LATA" &&
                      dialogDisciplines.length === 0) ||
                    (assessorType === "OTHER" && dialogDisciplines.length === 0)
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                      boxShadow: "0 6px 16px rgba(102,126,234,0.6)",
                    },
                    "&:disabled": {
                      background: "rgba(0,0,0,0.12)",
                      color: "rgba(0,0,0,0.26)",
                    },
                  }}
                >
                  {savingAssessor ? "Saving..." : "Submit"}
                </Button>
              </DialogActions>
            </Dialog>
            {/* CAB Contact Details Dialog - Enhanced Design */}
            <Dialog
              open={openCabDialog}
              onClose={() => setOpenCabDialog(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  pb: 2,
                  pt: 3,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      p: 1,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ContactIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      CAB Contact Details
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3, pb: 2, mt: 2 }}>
                <Grid container spacing={3}>
                  {/* Contact Person Card */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2.5,
                        bgcolor: "#f8f9ff",
                        borderRadius: 2,
                        border: "1px solid #e3e8ff",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            bgcolor: "#667eea",
                            color: "white",
                            p: 1,
                            borderRadius: 1.5,
                            mr: 1.5,
                          }}
                        >
                          <ContactIcon fontSize="small" />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#667eea" }}
                        >
                          Contact Person
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 0.5 }}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Name
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {labDetails.CpName || "Mr. SYED MAHABOOB IMRAN"}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Mobile Number
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {labDetails.CpMobile || "6364469163"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Email Address
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#667eea",
                              wordBreak: "break-word",
                            }}
                          >
                            {labDetails.CpEmail || "biocipher.ai@gmail.com"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Chief Executive Card */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2.5,
                        bgcolor: "#fff8f0",
                        borderRadius: 2,
                        border: "1px solid #ffe8cc",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            bgcolor: "#ff9800",
                            color: "white",
                            p: 1,
                            borderRadius: 1.5,
                            mr: 1.5,
                          }}
                        >
                          <ContactIcon fontSize="small" />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#ff9800" }}
                        >
                          Chief Executive
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 0.5 }}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Name
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {labDetails.NablName || "Dr. Gangadhar BHM"}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Designation
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {labDetails.NablDesignation || "Chief Executive"}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Mobile Number
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {labDetails.NablMobile || "-"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Email Address
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#ff9800",
                              wordBreak: "break-word",
                            }}
                          >
                            {labDetails.NablEmail || "gangubhm@gmail.com"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                <Button
                  onClick={() => setOpenCabDialog(false)}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 4,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                    },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteConfirmDialog.open}
              onClose={() =>
                setDeleteConfirmDialog({ open: false, assessorId: null })
              }
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this assessor allocation?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() =>
                    setDeleteConfirmDialog({ open: false, assessorId: null })
                  }
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteAssessor}
                  color="error"
                  variant="contained"
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {/* Reject Assessor Dialog */}
            <Dialog
              open={rejectAssessorDialog.open}
              onClose={() =>
                setRejectAssessorDialog({
                  open: false,
                  assessorId: null,
                  assessor: null,
                  reason: "",
                })
              }
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  background:
                    "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                Reject Assessor
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {rejectAssessorDialog.assessor && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {rejectAssessorDialog.assessor.assessor}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Capacity: {rejectAssessorDialog.assessor.capacity}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration:{" "}
                      {new Date(
                        rejectAssessorDialog.assessor.startDate,
                      ).toLocaleDateString()}{" "}
                      -{" "}
                      {new Date(
                        rejectAssessorDialog.assessor.endDate,
                      ).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Reason for Rejection *"
                  value={rejectAssessorDialog.reason}
                  onChange={(e) =>
                    setRejectAssessorDialog((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  placeholder="Please provide a detailed reason for rejecting this assessor..."
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  * This reason will be recorded and may be shared with relevant
                  stakeholders.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                  onClick={() =>
                    setRejectAssessorDialog({
                      open: false,
                      assessorId: null,
                      assessor: null,
                      reason: "",
                    })
                  }
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmRejectAssessor}
                  color="warning"
                  variant="contained"
                  disabled={!rejectAssessorDialog.reason.trim()}
                  sx={{
                    background:
                      "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)",
                    },
                  }}
                >
                  Reject Assessor
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Assessor Dialog */}
            <Dialog
              open={editAssessorDialog.open}
              onClose={() =>
                setEditAssessorDialog({ open: false, assessor: null })
              }
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                  p: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <EditIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Edit Assessor
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {editAssessorDialog.assessor && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Assessor Name"
                        value={editAssessorDialog.assessor.assessor || ""}
                        size="small"
                        InputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={
                          editAssessorDialog.assessor.startDate?.split(
                            "T",
                          )[0] || ""
                        }
                        onChange={(e) =>
                          setEditAssessorDialog((prev) => ({
                            ...prev,
                            assessor: {
                              ...prev.assessor,
                              startDate: e.target.value,
                            },
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={
                          editAssessorDialog.assessor.endDate?.split("T")[0] ||
                          ""
                        }
                        onChange={(e) =>
                          setEditAssessorDialog((prev) => ({
                            ...prev,
                            assessor: {
                              ...prev.assessor,
                              endDate: e.target.value,
                            },
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    {editAssessorDialog.assessor.capacity === "LA" ||
                    editAssessorDialog.assessor.capacity === "LA_TA" ? (
                      <>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Capacity</InputLabel>
                            <Select
                              value={editAssessorDialog.assessor.capacity || ""}
                              label="Capacity"
                              onChange={(e) =>
                                setEditAssessorDialog((prev) => ({
                                  ...prev,
                                  assessor: {
                                    ...prev.assessor,
                                    capacity: e.target.value,
                                  },
                                }))
                              }
                            >
                              <MenuItem value="LA">Lead Assessor</MenuItem>
                              <MenuItem value="LA_TA">
                                Lead + Technical Assessor
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {editAssessorDialog.assessor.capacity === "LA_TA" && (
                          <Grid item xs={12}>
                            <Autocomplete
                              multiple
                              size="small"
                              options={disciplineList}
                              getOptionLabel={(option) => option.name}
                              value={disciplineList.filter((d) =>
                                (
                                  editAssessorDialog.assessor.disciplineIds ||
                                  []
                                ).includes(d.id),
                              )}
                              onChange={(event, newValue) => {
                                setEditAssessorDialog((prev) => ({
                                  ...prev,
                                  assessor: {
                                    ...prev.assessor,
                                    disciplineIds: newValue.map((v) => v.id),
                                    disciplines: newValue.map((v) => v.name),
                                  },
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Discipline"
                                  placeholder="Select disciplines"
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Capacity</InputLabel>
                            <Select
                              value={editAssessorDialog.assessor.capacity || ""}
                              label="Capacity"
                              onChange={(e) =>
                                setEditAssessorDialog((prev) => ({
                                  ...prev,
                                  assessor: {
                                    ...prev.assessor,
                                    capacity: e.target.value,
                                  },
                                }))
                              }
                            >
                              <MenuItem value="OBS">Observer</MenuItem>
                              <MenuItem value="TA">Technical Assessor</MenuItem>
                              <MenuItem value="TE">Technical Expert</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            size="small"
                            options={disciplineList}
                            getOptionLabel={(option) => option.name}
                            value={disciplineList.filter((d) =>
                              (
                                editAssessorDialog.assessor.disciplineIds || []
                              ).includes(d.id),
                            )}
                            onChange={(event, newValue) => {
                              setEditAssessorDialog((prev) => ({
                                ...prev,
                                assessor: {
                                  ...prev.assessor,
                                  disciplineIds: newValue.map((v) => v.id),
                                  disciplines: newValue.map((v) => v.name),
                                },
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Discipline"
                                placeholder="Select disciplines"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </DialogContent>
              <DialogActions
                sx={{ p: 2.5, gap: 1.5, background: "rgba(0,0,0,0.02)" }}
              >
                <Button
                  onClick={() =>
                    setEditAssessorDialog({ open: false, assessor: null })
                  }
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateAssessor}
                  variant="contained"
                  disabled={editingAssessor}
                  startIcon={
                    editingAssessor ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    color: "white",
                    boxShadow: "0 4px 12px rgba(240,147,251,0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #e082ea 0%, #e4465b 100%)",
                      boxShadow: "0 6px 16px rgba(240,147,251,0.6)",
                    },
                    "&:disabled": {
                      background: "rgba(0,0,0,0.12)",
                      color: "rgba(0,0,0,0.26)",
                    },
                  }}
                >
                  {editingAssessor ? "Updating..." : "Update"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Rollback Confirmation Dialog */}
            <Dialog
              open={rollbackConfirmDialog.open}
              onClose={() =>
                !rollbackConfirmDialog.loading &&
                setRollbackConfirmDialog({ open: false, loading: false })
              }
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <DialogTitle
                sx={{
                  background:
                    "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                  color: "white",
                  textAlign: "center",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                }}
              >
                ⚠️ Confirm Rollback Assessment
              </DialogTitle>
              <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    Are you sure you want to rollback the entire assessment?
                  </Typography>
                  <Typography variant="body2">This action will:</Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    <li>Remove all allocated assessors</li>
                    <li>Lab and Assessors Needs to Accept Again</li>
                    <li>Cannot be undone</li>
                  </Box>
                </Alert>
              </DialogContent>
              <DialogActions
                sx={{ p: 2.5, gap: 1.5, background: "rgba(0,0,0,0.02)" }}
              >
                <Button
                  onClick={() =>
                    setRollbackConfirmDialog({ open: false, loading: false })
                  }
                  variant="outlined"
                  disabled={rollbackConfirmDialog.loading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmRollbackAssessment}
                  variant="contained"
                  disabled={rollbackConfirmDialog.loading}
                  startIcon={
                    rollbackConfirmDialog.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <WarningIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                    background:
                      "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                    color: "white",
                    boxShadow: "0 4px 12px rgba(255,107,107,0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #ff5252 0%, #d63031 100%)",
                      boxShadow: "0 6px 16px rgba(255,107,107,0.6)",
                    },
                    "&:disabled": {
                      background: "rgba(0,0,0,0.12)",
                      color: "rgba(0,0,0,0.26)",
                    },
                  }}
                >
                  {rollbackConfirmDialog.loading
                    ? "Rolling back..."
                    : "Yes, Rollback"}
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssessorAllotment;